import CompassCurrent from "@components/compass/CompassCurrent"
import CompassWind from "@components/compass/CompassWind"
import { CompassTimeseriesData } from "@components/compass/types"
import { isFeatureAvailable, useGetDevFeatures } from "@components/featureGuard/FeatureGuard"
import useGetFishFarmDataForMap from "@components/map/dataGettterHooks/useGetFishFarmDataForMap"
import useGetOilGasDataForMap from "@components/map/dataGettterHooks/useGetOilGasDataForMap"
import RegularMapComponent3 from "@components/map/regularMap/RegularMapComponent"
import { useGetVisibleFacilities } from "@components/map/useGetVisibleFacilities"
import { LayerData, MapLayers } from "@components/map/utils/mapLayers"
import Modal from "@components/modal/Modal"
import PlotCard from "@components/plot/PlotCard"
import PlotConfig from "@components/plot/plotConfig/PlotConfig"
import { UserManual } from "@components/userManual"
import {
	operationalEventScrollRules,
	operationalEventSteps,
} from "@components/userManual/moduleSteps/OperationsManual"
import { ImportantIcon } from "@helpers/ImportantIcon"
import { debounce } from "@helpers/debounce"
import { getSensorIdWithoutComponent } from "@helpers/getSensorIdWithoutComponent"
import { getColorOfOpModeName, opModeNameColors } from "@helpers/opModeReferences"
import { useGetOpModesListQuery } from "@queries/useGetOpModesListQuery"
import { useAppSelector } from "@redux/app/hooks"
import { useAppStructure } from "@redux/features/appStructure/useAppStructure"
import { getCurrentCompany } from "@redux/features/currentCompany/currentCompanySlice"
import {
	getDiseaseZones,
	getSelectedDiseaseZones,
} from "@redux/features/diseaseZones/diseaseZonesSlice"
import { PlantInfoValidated } from "@redux/features/listOfVessels/listOfVesselsSlice"
import { getMapParamsState } from "@redux/features/mapParams/mapParamsSlice"
import { TimelineEventMarker } from "@redux/features/timelineEventMarker/timelineEventMarkerSlice"
import { getUserData } from "@redux/features/userData/userDataSlice"
import { getTrack } from "@sop/FuelPage/Total/utils/track"
import { useQuery } from "@tanstack/react-query"
import { useSearch } from "@tanstack/react-router"
import { Row } from "@tanstack/react-table"
import { VerticalLinePluginProps } from "@ui-lib/core/chartJS/plugins/verticalLine"
import { Button, IconButton, LoadingCircleSimples, Tooltip as TooltipButton } from "@ui-lib/src"
import { COLORS_PLOTS_COLORBLIND } from "@utils/colorsPlots"
import { common } from "@utils/common"
import { requestApi2 } from "@utils/http"
import {
	AggregationMethod,
	FuelPageGetBaselineDataResponse,
	GetPlantMaxKwArgs,
	GetPlantMaxKwResponse,
	PlantPlot,
	PortalFeature,
	Segment,
	VesselTrack,
	endpoints,
} from "api"
import { ChartData, Chart as ChartJS, ChartOptions } from "chart.js"
import { RefObject, memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Line } from "react-chartjs-2"
import { useTranslation } from "react-i18next"
import { MdAutoStories, MdVisibility, MdVisibilityOff } from "react-icons/md"
import { Step } from "react-joyride"
import {
	COMBINED_MODES,
	EventTableEntry,
	FuelConsumptionTimelineTableItem,
} from "./OperationsTimelineContent"
import { isAquacultureTimelineCompany } from "./aquacultureTimeline"
import {
	FISH_LOG_LOOKBACK_DAYS_DEFAULT,
	buildFishModeBgRanges,
	buildFishModeColorMap,
	buildFishModeLegendItems,
	getFishModesAtTimestamp,
} from "./fishLogEvents"
import { useFishLogEventsWithLookback } from "./queries/useFishLogEventsQuery"
import { useGetTimeseries4Query } from "./queries/useGetTimeseries4Query"

export interface SensorData {
	id: number
	data: [number, number | null][]
}

let leftEnd: number
let rightEnd: number

const uniqueObjects = <T extends Object>(arr: T[]) => {
	const seen = new Map()
	return arr.filter((obj) => {
		const key = Object.entries(obj).sort().toString()
		if (seen.has(key)) return false
		seen.set(key, obj)
		return true
	})
}

// created by AI
function uniqueNumbers(values: number[]): number[] {
	return Array.from(new Set(values))
}

// created by AI
function setCssColorAlpha(color: string, alpha: number): string {
	const rgbaMatch = color.match(/^rgba\(([^,]+),\s*([^,]+),\s*([^,]+),\s*[^)]+\)$/)
	if (rgbaMatch !== null) {
		return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${alpha})`
	}

	const rgbMatch = color.match(/^rgb\(([^,]+),\s*([^,]+),\s*([^)]+)\)$/)
	if (rgbMatch !== null) {
		return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${alpha})`
	}

	return color
}

function updateChartTimeRange(charts: (ChartJS | null)[]) {
	charts.forEach((chart) => {
		if (chart === null) {
			return
		}
		if (chart.options.scales !== undefined && chart.options.scales.x !== undefined) {
			chart.options.scales.x.min = leftEnd
			chart.options.scales.x.max = rightEnd
			chart.update("zoom")
		}
	})
}

type GetVesselTrackQueryArgs = {
	plantId: number
	t1: Date | undefined
	t2: Date | undefined
}
export function useGetVesselTrackQuery(args: GetVesselTrackQueryArgs) {
	const res = useQuery({
		queryKey: [
			`queryId=GetVesselTrack`,
			`endpoints=[${endpoints.getPlantLocationTrack.getName()}]`,
			`plantId=${args.plantId}`,
			`t1=${args.t1?.toISOString !== undefined ? args.t1.toISOString() : args.t1}`,
			`t2=${args.t2?.toISOString !== undefined ? args.t2.toISOString() : args.t2}`,
		],
		queryFn: async (): Promise<VesselTrack | undefined> => {
			if (args.plantId === -1 || args.t1 === undefined || args.t2 === undefined) {
				return undefined
			}
			const response = await requestApi2(endpoints.getPlantLocationTrack, {
				plantId: args.plantId,
				interval: { t1: args.t1, t2: args.t2 },
			})
			return interpolateVesselTrackAngles(response) ?? undefined
		},
		staleTime: 600000,
		refetchInterval: 600000,
	})
	return res
}
const track = getTrack()
type VesselTrackLocation = VesselTrack["locations"][number]
type VesselTrackAngleField = "course" | "heading"

const normalizeDegrees = (angle: number) => {
	const normalized = angle % 360
	return normalized < 0 ? normalized + 360 : normalized
}

const getShortestAngleDeltaDegrees = (from: number, to: number) => {
	const delta = normalizeDegrees(to) - normalizeDegrees(from)
	if (delta > 180) {
		return delta - 360
	}
	if (delta < -180) {
		return delta + 360
	}
	return delta
}

const interpolateDegrees = (from: number, to: number, progress: number) => {
	return normalizeDegrees(from + getShortestAngleDeltaDegrees(from, to) * progress)
}

const getKnownAngleIndexes = (locations: VesselTrackLocation[], field: VesselTrackAngleField) => {
	return locations.flatMap((location, index) => (location[field] === null ? [] : [index]))
}

const getInterpolatedAngle = (
	locations: VesselTrackLocation[],
	field: VesselTrackAngleField,
	index: number,
	previousIndex: number,
	nextIndex: number | undefined
) => {
	const previousLocation = locations[previousIndex]
	if (previousLocation === undefined) {
		return null
	}

	const previousAngle = previousLocation[field]
	if (previousAngle === null) {
		return null
	}

	if (index < previousIndex || nextIndex === undefined) {
		return previousAngle
	}

	const nextLocation = locations[nextIndex]
	const nextAngle = nextLocation?.[field]
	if (nextLocation === undefined || nextAngle === null || nextAngle === undefined) {
		return previousAngle
	}

	const currentLocation = locations[index]
	if (currentLocation === undefined) {
		return previousAngle
	}

	const previousTimestamp = previousLocation.timestamp.getTime()
	const nextTimestamp = nextLocation.timestamp.getTime()
	if (previousTimestamp === nextTimestamp) {
		return previousAngle
	}

	const progress =
		(currentLocation.timestamp.getTime() - previousTimestamp) /
		(nextTimestamp - previousTimestamp)

	return interpolateDegrees(previousAngle, nextAngle, progress)
}

const interpolateVesselTrackAngleField = (
	locations: VesselTrackLocation[],
	field: VesselTrackAngleField
) => {
	const knownAngleIndexes = getKnownAngleIndexes(locations, field)
	if (knownAngleIndexes.length === 0) {
		return locations
	}

	let knownAngleCursor = 0
	return locations.map((location, index) => {
		if (location[field] !== null) {
			return location
		}

		while (knownAngleCursor < knownAngleIndexes.length - 1) {
			const nextKnownAngleIndex = knownAngleIndexes[knownAngleCursor + 1]
			if (nextKnownAngleIndex === undefined || nextKnownAngleIndex >= index) {
				break
			}
			knownAngleCursor++
		}

		const previousKnownAngleIndex = knownAngleIndexes[knownAngleCursor]
		if (previousKnownAngleIndex === undefined) {
			return location
		}

		return {
			...location,
			[field]: getInterpolatedAngle(
				locations,
				field,
				index,
				previousKnownAngleIndex,
				knownAngleIndexes[knownAngleCursor + 1]
			),
		}
	})
}

const interpolateVesselTrackAngles = (vesselTrack: VesselTrack | null) => {
	if (vesselTrack === null) {
		return null
	}

	return {
		...vesselTrack,
		locations: interpolateVesselTrackAngleField(
			interpolateVesselTrackAngleField(vesselTrack.locations, "course"),
			"heading"
		),
	}
}

type GetVesselTrackQueryV2Args = {
	plantId: number
	t1: Date | undefined
	t2: Date | undefined
	refreshQuery?: boolean
}
export function useGetVesselTrackQueryV2(args: GetVesselTrackQueryV2Args) {
	return useQuery({
		queryKey: [
			`queryId=GetVesselTrackV2`,
			`endpoints=[${endpoints.getPlantLocationTrack.getName()}]`,
			`plantId=${args.plantId}`,
			`t1=${
				args.t1 !== undefined
					? `${args.t1.toISOString().split(":")[0]}:${
							args.t1.toISOString().split(":")[1]
						}`
					: "no date from"
			}`,
			`t2=${
				args.t2 !== undefined
					? `${args.t2.toISOString().split(":")[0]}:${
							args.t2.toISOString().split(":")[1]
						}`
					: "no date to"
			}`,
			`refreshQuery=${args.refreshQuery === true ? "true" : "false"}`,
		],
		queryFn: async (): Promise<VesselTrack | null> => {
			if (args.plantId === -1 || args.t1 === undefined || args.t2 === undefined) {
				throw new Error("Invalid query params")
			}

			const toTimestamp = new Date()
			if (args.refreshQuery !== true) {
				toTimestamp.setTime(args.t2.getTime())
			}

			const vesselTrack = await requestApi2(endpoints.getPlantLocationTrack, {
				plantId: args.plantId,
				interval: { t1: args.t1, t2: toTimestamp },
			})
			return interpolateVesselTrackAngles(vesselTrack)
		},
		enabled: args.t1 !== undefined && args.t2 !== undefined && args.plantId !== -1,
		staleTime: args.refreshQuery === true ? 60 * 1000 : 60 * 60 * 1000,
		refetchInterval: args.refreshQuery === true ? 60 * 1000 : 60 * 60 * 1000,
	})
}

type GetBaselineDataQueryArgs = { plantId: number; t1: Date; t2: Date }
export function useGetBaselineDataQuery(args: GetBaselineDataQueryArgs) {
	const res = useQuery({
		queryKey: [
			`queryId=GetBaselineData`,
			`endpoints=[${endpoints.fuelPageGetBaselineData.getName()}]`,
			`plantId=${args.plantId}`,
			`t1=${args.t1?.toISOString !== undefined ? args.t1.toISOString() : args.t1}`,
			`t2=${args.t2?.toISOString !== undefined ? args.t2.toISOString() : args.t2}`,
		],
		queryFn: async (): Promise<FuelPageGetBaselineDataResponse | undefined> => {
			if (args.plantId === -1) {
				return undefined
			}
			if (args.t1 === undefined || args.t2 === undefined) {
				return undefined
			}

			const response = await requestApi2(endpoints.fuelPageGetBaselineData, {
				plantId: args.plantId,
				t1: args.t1,
				t2: args.t2,
			})
			return response !== null ? response : undefined
		},
		staleTime: 600000,
		refetchInterval: 600000,
	})
	return res
}

function useFuelPageGetPlantMaxKwArgs(args: GetPlantMaxKwArgs) {
	const res = useQuery({
		queryKey: [
			`queryId=GetPlantMaxKwArgs`,
			`endpoints=[${endpoints.hydrodynamicPerformanceGetPlantMaxKw.getName()}]`,
			`plantIds=${args.plantIds}`,
		],
		queryFn: async (): Promise<GetPlantMaxKwResponse | undefined | null> => {
			if (args.plantIds === undefined) return undefined

			const response = await requestApi2(endpoints.hydrodynamicPerformanceGetPlantMaxKw, {
				plantIds: args.plantIds,
			})
			return response
		},
		staleTime: 600000,
		refetchInterval: 600000,
	})
	return res
}

const findWhichCombineMode = (
	opModesList: {
		name: string
		id: number
	}[],
	refOpModeId: number
) => {
	const refOpModeName = opModesList
		.find((opModeData) => opModeData.id === refOpModeId)
		?.name.toLowerCase()

	if (refOpModeName === undefined) {
		return undefined
	}
	for (const combinedMode of COMBINED_MODES) {
		if (refOpModeName.includes(combinedMode.refString) === true) {
			return combinedMode
		}
	}
	return undefined
}

export const ChartContainer = memo(
	(props: {
		plotData: ChartData<"line"> | undefined
		options: ChartOptions<"line"> | undefined
		minY?: number
		maxY?: number
		heightExpression: string
		minHeightExpression?: string
		chartRef: RefObject<ChartJS<"line"> | null>
		chartKey?: string
	}) => {
		return props.plotData !== undefined &&
			props.options !== undefined &&
			props.plotData.datasets.length > 0 ? (
			<div
				className={`w-full rounded-sm border-1`}
				style={{
					height: props.heightExpression,
					minHeight: props.minHeightExpression,
				}}
			>
				<Line
					key={props.chartKey}
					ref={props.chartRef}
					options={{
						...props.options,
						scales: {
							...props.options.scales,
							y: {
								...props.options.scales?.y,
								min: props.minY,
								max: props.maxY,
							},
						},
					}}
					data={props.plotData}
				/>
			</div>
		) : null
	}
)

export const generateDefaultOptions = (
	chart: RefObject<ChartJS<"line"> | null>,
	otherCharts: RefObject<ChartJS<"line"> | null>[],
	yAxis: { label: string; position?: "left" | "right" }[],
	minFrom: number,
	maxTo: number,
	timestampGetter: (tRef: number) => void,
	units?: { label: string; unit: string }[],
	bgColors?: {
		colorMap: {
			[modeName: string]: string
		}
		ranges: [number, number, string][]
	},
	syncGroupId?: number | string,
	verticalLine?: VerticalLinePluginProps,
	animate?: boolean
): ChartOptions<"line"> => {
	return {
		// responsive: true,
		animation: animate === false ? false : undefined,
		maintainAspectRatio: false,
		interaction: {
			mode: "index",
			intersect: false,
		},

		plugins: {
			tooltip: {
				position: "average",
				animation: { duration: 350, delay: 10 },
				callbacks: {
					label(tooltipItem) {
						const label = tooltipItem.dataset.label
						const value = tooltipItem.parsed.y
						return `${label}: ${typeof value === "number" ? value.toFixed(1) : value} ${
							units?.find((u) => u.label === label)?.unit ?? ""
						}`
					},
				},
				intersect: false,
				mode: "index",
			},
			legend: {
				position: "bottom",
			},
			title: {
				display: false,
			},
			zoom: {
				limits: {
					x: {
						min: minFrom,
						max: maxTo,
					},
				},
				pan: {
					enabled: true,
					mode: "x",
					onPan: function () {
						const chartObj = chart.current
						if (chartObj === null) {
							return
						}
						const xScale = chartObj.scales.x
						if (xScale === undefined) {
							return
						}
						leftEnd = xScale.min
						rightEnd = xScale.max
						updateChartTimeRange([...otherCharts.map((chart) => chart.current)])
					},
				},
				zoom: {
					wheel: {
						enabled: true,
					},
					pinch: {
						enabled: true,
					},
					onZoom: function () {
						const chartObj = chart.current
						if (chartObj === null) {
							return
						}
						const xScale = chartObj.scales.x
						if (xScale === undefined) {
							return
						}
						leftEnd = xScale.min
						rightEnd = xScale.max
						updateChartTimeRange([...otherCharts.map((chart) => chart.current)])
					},
					mode: "x",
				},
			},
			bgColor: bgColors ?? {
				colorMap: {},
				ranges: [],
			},
			verticalLine: verticalLine,
			crosshairV2: {
				timestampGetter: timestampGetter,
				sync: {
					enabled: true,
					group: syncGroupId ?? minFrom + maxTo,
					suppressTooltips: false,
				},
			},
		},
		scales: yAxis.reduce(
			(acc, curr, idx) => {
				acc[`y${idx === 0 ? "" : idx}`] = {
					position: curr.position,
					grid: { display: idx > 0 ? false : true },
					title: {
						display: true,
						text: curr.label,
					},
				}
				return acc
			},
			{
				x: {
					type: "time",
					time: {
						displayFormats: {
							hour: "HH:mm",
							minute: "HH:mm",
							second: "HH:mm:ss",
						},
						tooltipFormat: "MMM dd, yyyy, HH:mm:ss",
					},
					title: {
						display: false,
						text: "time",
					},
				},
			}
		),
	}
}

function ComponentBody({
	eventData,
	eventData_0,
}: {
	eventData: EventTableEntry[]
	eventData_0: EventTableEntry
}) {
	const THRESHOLD_POWER = 1 // %
	const baselineName =
		eventData_0.metadata.baselineConfigName !== null
			? `${eventData_0.metadata.baselineConfigName} `
			: ""
	const baselineUnit =
		eventData_0.metadata.baselineUnit !== null ? `${eventData_0.metadata.baselineUnit} ` : ""
	const { t, i18n } = useTranslation()
	const { data: appStructure } = useAppStructure()

	const { facilitiesData } = useGetVisibleFacilities()

	const paramSelectedVessel = useSearch({ strict: false })?.vessel ?? ""

	const currentCompany = useAppSelector(getCurrentCompany)
	const userData = useAppSelector(getUserData)
	const devFeatures = useGetDevFeatures()
	const plantData = common.plantDataFromOwnerAndName.get(
		`${currentCompany?.toLowerCase()}/${paramSelectedVessel?.toLowerCase()}`
	)

	const eventStartTimestamp = Math.min(...eventData.map((e) => new Date(e.event.t1).getTime()))
	const eventEndTimestamp = Math.max(...eventData.map((e) => new Date(e.event.t2).getTime()))
	const t1EventRange = useMemo(() => new Date(eventStartTimestamp), [eventStartTimestamp])
	const t2EventRange = useMemo(() => new Date(eventEndTimestamp), [eventEndTimestamp])
	const conditionSensorIds = useMemo(
		() => uniqueNumbers(eventData.flatMap((entry) => entry.metadata.conditionSensorsId)),
		[eventData]
	)
	const fuelConsumerSensorIds = useMemo(
		() => uniqueNumbers(eventData.flatMap((entry) => entry.metadata.fuelConsumersSensorsId)),
		[eventData]
	)
	const loadSensorIds = useMemo(
		() => uniqueNumbers(eventData.flatMap((entry) => entry.metadata.loadSensorsId)),
		[eventData]
	)
	const weatherSensorIds = useMemo(
		() => uniqueNumbers(eventData.flatMap((entry) => entry.metadata.weatherSensorsId)),
		[eventData]
	)
	const powerProducers = useMemo(
		() => uniqueObjects(eventData.flatMap((entry) => entry.metadata.powerProducers)),
		[eventData]
	)
	const powerProducerKwSensorIds = useMemo(
		() => uniqueNumbers(powerProducers.map((producer) => producer.kwSensorId)),
		[powerProducers]
	)
	const opModeIds = useMemo(
		() => uniqueNumbers(eventData.map((entry) => entry.metadata.opModeId)),
		[eventData]
	)
	const representativeOpModeId = eventData_0.metadata.opModeId
	const canUserSeeAquacultureColumns =
		currentCompany !== undefined &&
		isAquacultureTimelineCompany(currentCompany) &&
		isFeatureAvailable(
			undefined,
			PortalFeature.AQUACULTURE,
			currentCompany,
			devFeatures,
			userData.portalMode
		)
	const { normalizedEvents: fishLogEventsNormalized } = useFishLogEventsWithLookback({
		plantId: plantData?.id,
		company: currentCompany,
		gte: t1EventRange,
		lt: t2EventRange,
		lookbackDays: FISH_LOG_LOOKBACK_DAYS_DEFAULT,
		enabled: canUserSeeAquacultureColumns,
	})

	const [plotZoom, setPlotZoom] = useState<
		{
			plotId: number
			zoomFrom?: number
			zoomTo?: number
		}[]
	>()

	const hybridPropulsionPowerID = getSensorIdWithoutComponent(
		plantData,
		"Hybrid Propulsion Power"
	)

	const {
		isLoading: isOpModesListLoading,
		isError: isOpModesListError,
		data: opModesList,
	} = useGetOpModesListQuery({ plantIdsList: [plantData?.id ?? -1] })

	const combinedModeMetaData = findWhichCombineMode(opModesList ?? [], representativeOpModeId)
	const findOpModeName = (opModeId: number) => {
		return opModesList?.find((opModeData) => opModeData.id === opModeId)?.name
	}

	const {
		isLoading: isVesselTrackLoading,
		isError: isVesselTrackError,
		data: vesselTrack,
	} = useGetVesselTrackQuery({
		plantId: plantData?.id ?? -1,
		t1: t1EventRange,
		t2: t2EventRange,
	})

	const {
		isLoading: isPlantMaxKwLoading,
		isError: isPlantMaxKwError,
		data: plantsMaxKw,
	} = useFuelPageGetPlantMaxKwArgs({
		plantIds: [plantData?.id ?? -1],
	})

	const [time, setTime] = useState<number>()

	const timestampGetter = useCallback(
		debounce((tRef: number) => {
			if (time === tRef) {
				return
			}
			setTime(tRef)
		}, 0),
		[]
	)

	useEffect(() => {
		if (time === undefined) {
			return
		}
		const tRef = time

		const trackPoints = vesselTrack?.locations
		if (trackPoints === undefined) {
			return
		}
		for (let i = 0; i < trackPoints.length; i++) {
			const trackPoint = trackPoints[i]
			if (trackPoint === undefined) {
				continue
			}
			if (i === 0) {
				if (trackPoint.timestamp.getTime() >= tRef) {
					setTimelineEventMarker({
						coords: {
							lat: trackPoint.latitude,
							lon: trackPoint.longitude,
						},
						heading: trackPoint.heading ?? trackPoint.course,
					})
				}
			} else if (i === trackPoints.length - 1) {
				if (trackPoint.timestamp.getTime() <= tRef) {
					setTimelineEventMarker({
						coords: {
							lat: trackPoint.latitude,
							lon: trackPoint.longitude,
						},
						heading: trackPoint.heading ?? trackPoint.course,
					})
				}
			} else {
				const trackPointPrev = trackPoints[i - 1]
				const trackPointNext = trackPoints[i + 1]
				if (trackPointPrev === undefined || trackPointNext === undefined) {
					continue
				}
				if (
					trackPointPrev.timestamp.getTime() <= tRef &&
					trackPointNext.timestamp.getTime() >= tRef
				) {
					setTimelineEventMarker({
						coords: {
							lat: trackPoint.latitude,
							lon: trackPoint.longitude,
						},
						heading: trackPoint.heading ?? trackPoint.course,
					})
				}
			}
		}
	}, [time])

	const conditionChartRef = useRef<ChartJS<"line">>(null)
	const fuelChartRef = useRef<ChartJS<"line">>(null)
	const baselineChartRef = useRef<ChartJS<"line">>(null)
	const loadChartRef = useRef<ChartJS<"line">>(null)

	const [showOpModeBg, setShowOpModeBg] = useState<boolean | undefined>(true)
	const [lastZoomScale, setLastZoomScale] = useState<[number, number] | undefined>()
	const [updateChart, setUpdateChart] = useState<boolean>(false)

	useEffect(() => {
		const xScale = conditionChartRef?.current?.scales["x"]
		if (conditionChartRef.current !== null && xScale !== undefined) {
			setLastZoomScale([xScale.min, xScale.max])
			setUpdateChart(true)
		}
	}, [showOpModeBg])

	const updateChartZoom = (
		chartRef: React.RefObject<ChartJS<"line"> | null>,
		zoomScale: [number, number] | undefined
	) => {
		if (
			chartRef.current !== null &&
			zoomScale !== undefined &&
			chartRef.current.options.scales !== undefined
		) {
			const [min, max] = zoomScale
			const chart = chartRef.current
			if (chart.options.scales?.x !== undefined) {
				chart.options.scales.x.min = min
				chart.options.scales.x.max = max
				chart.update("zoom")
			}
		}
	}

	const bgColorRange = useMemo(
		() =>
			eventData.map((e) => {
				return [
					new Date(e.event.t1).setUTCSeconds(0, 0),
					new Date(e.event.t2).setUTCSeconds(0, 0),
					findOpModeName(e.metadata.opModeId)?.toLowerCase() ?? "(na)",
				]
			}),
		[eventData, opModesList]
	)
	const modeColors = useMemo(
		() =>
			Object.fromEntries(
				Object.entries(opModeNameColors).map(([key, value]) => [key, `${value}30`])
			),
		[]
	)

	const fishModeBgRanges: [number, number, string][] = useMemo(() => {
		if (canUserSeeAquacultureColumns === false) {
			return []
		}
		return buildFishModeBgRanges(fishLogEventsNormalized, t1EventRange, t2EventRange)
	}, [canUserSeeAquacultureColumns, fishLogEventsNormalized, t1EventRange, t2EventRange])

	const fishModeColorMap = useMemo(() => {
		if (canUserSeeAquacultureColumns === false) {
			return {}
		}
		return buildFishModeColorMap(fishLogEventsNormalized)
	}, [canUserSeeAquacultureColumns, fishLogEventsNormalized])

	const fishModeLegendItems = useMemo(() => {
		const activeFishKeys = new Set(fishModeBgRanges.map((range) => range[2]))
		return buildFishModeLegendItems(fishLogEventsNormalized, fishModeColorMap, activeFishKeys)
	}, [fishLogEventsNormalized, fishModeBgRanges, fishModeColorMap])

	const conditionBgColors = useMemo(
		() => ({
			colorMap: {
				...modeColors,
				...fishModeColorMap,
			},
			ranges: [...bgColorRange, ...fishModeBgRanges],
		}),
		[modeColors, fishModeColorMap, bgColorRange, fishModeBgRanges]
	)
	const operationModeBgColors = useMemo(
		() => ({
			colorMap: modeColors,
			ranges: bgColorRange,
		}),
		[modeColors, bgColorRange]
	)
	const hasOperationModeEvents = bgColorRange.length > 0
	const hasConditionBgRanges = conditionBgColors.ranges.length > 0
	const hasFishModeInRange = fishModeLegendItems.length > 0

	// CONDITION DATA PLOT
	const [conditionLinePlotOptions, setConditionLinePlotOptions] = useState<ChartOptions<"line">>()
	const [conditionLinePlotData, setConditionLinePlotData] = useState<ChartData<"line">>()

	// LOAD DATA PLOT
	const [loadLinePlotOptions, setLoadLinePlotOptions] = useState<ChartOptions<"line">>(
		generateDefaultOptions(
			loadChartRef,
			[fuelChartRef, conditionChartRef, baselineChartRef],
			[{ label: `${t("POWER")} (kW)` }],
			eventStartTimestamp,
			eventEndTimestamp,
			timestampGetter,
			loadSensorIds.map((sensorId) => {
				const sensorData = common.sensorDataFromIds.get(sensorId)
				if (sensorData === undefined) {
					return { label: "", unit: "" }
				}
				return { label: sensorData.display_name, unit: sensorData.unit }
			})
		)
	)
	const [loadLinePlotData, setLoadLinePlotData] = useState<ChartData<"line">>()
	const [minLoad, setMinLoad] = useState<number>(0)

	// FUEL CONSUMPTION PLOT
	const [fuelLinePlotOptions, setFuelLinePlotOptions] = useState<ChartOptions<"line">>(
		generateDefaultOptions(
			fuelChartRef,
			[conditionChartRef, loadChartRef, baselineChartRef],
			[{ label: `${t("LOAD")} (%)` }],
			eventStartTimestamp,
			eventEndTimestamp,
			timestampGetter,
			[
				...fuelConsumerSensorIds.map((sensorId) => {
					const sensorData = common.sensorDataFromIds.get(sensorId)
					if (sensorData === undefined) {
						return { label: "", unit: "" }
					}
					return { label: sensorData.display_name, unit: sensorData.unit }
				}),
				...powerProducers.map((producer) => {
					const sensorData = common.sensorDataFromIds.get(producer.kwSensorId)
					if (sensorData === undefined) {
						return { label: "", unit: "" }
					}
					const tempLabel = sensorData.display_name.split(" ")[0] + "Load %"
					return { label: tempLabel, unit: "%" }
				}),
			]
		)
	)

	// BASELINE CONSUMPTION PLOT
	const [baselinePlotOptions, setBaselinePlotOptions] = useState<ChartOptions<"line">>(
		generateDefaultOptions(
			baselineChartRef,
			[fuelChartRef, conditionChartRef, loadChartRef],
			[
				{ label: t("FUEL RATE (L/H)") },
				{
					label:
						baselineUnit.trim() !== "" && baselineUnit.includes("L/h") === false
							? `${t("BASELINE")} (${baselineUnit})`
							: "",
				},
				{ label: t("BASELINE DEVIATION (%)") },
			].filter((b) => b.label !== ""),
			eventStartTimestamp,
			eventEndTimestamp,
			timestampGetter,
			[
				{
					label: `${baselineName}${t("BASELINE")}${baselineUnit.includes("L/h") ? ` ${t("FUEL RATE").toLowerCase()}` : ""}`,
					unit: baselineUnit,
				},
				{
					label: `${baselineName}${t("ACTUAL")}${baselineUnit.includes("L/h") ? ` ${t("FUEL RATE").toLowerCase()}` : ""}`,
					unit: baselineUnit,
				},
				{ label: `${baselineName}${t("DEVIATION")}`, unit: "%" },
				{ label: t("TOTAL ENGINE FUEL RATE"), unit: "L/h" },
				{ label: t("TOTAL VESSEL FUEL RATE"), unit: "L/h" },
			]
		)
	)

	const [fuelLinePlotData, setFuelLinePlotData] = useState<ChartData<"line">>()
	const [baselinePlotData, setBaselinePlotData] = useState<ChartData<"line">>()
	const [minFuel, setMinFuel] = useState<number>(0)
	const [maxFuel, setMaxFuel] = useState<number | undefined>()
	const [minBaseline, setMinBaseline] = useState<number>(0)

	const {
		isLoading: isTimeseriesDataLoading,
		isError: isTimeseriesDataError,
		data: timeseriesData,
	} = useGetTimeseries4Query({
		gte: t1EventRange,
		lt: t2EventRange,
		sensorIds: [
			...conditionSensorIds,
			...fuelConsumerSensorIds,
			...loadSensorIds,
			...powerProducerKwSensorIds,
		],
	})

	const {
		isLoading: isBaselineDataLoading,
		isError: isBaselineDataError,
		data: baselineData,
	} = useGetBaselineDataQuery({
		plantId: plantData?.id ?? -1,
		t1: t1EventRange,
		t2: t2EventRange,
	})

	const [timeseriesDataV2, setTimeseriesDataV2] = useState<{
		sensors: {
			data: [number, number][]
			id: number
		}[]
	}>()
	const speedId = -5

	let plantMaxPowerProducedKw: number | undefined = undefined
	const maxKwProduced = powerProducers.flatMap((a) => a.maxKw).filter((f) => f !== null)
	if (maxKwProduced.length > 0) {
		plantMaxPowerProducedKw = maxKwProduced.reduce((acc, cur) => acc + cur, 0)
	}

	useEffect(() => {
		if (timeseriesData !== null && timeseriesData !== undefined) {
			// Creating a deep copy of timeseriesData to modify
			const timeseriesDataWithPowerPercent: {
				sensors: {
					data: [number, number][]
					id: number
				}[]
			} = {
				// ...timeseriesData,
				sensors: timeseriesData.sensors.map((sensor) => {
					// Check if sensor only has one data point and the point is before the event start time, set value to zero
					sensor.data = sensor.data.filter((d) => d[1] !== null)
					if (sensor.data.length === 1 && sensor.data[0]?.[0] !== undefined)
						if (sensor.data[0][0] < t1EventRange.getTime()) {
							return {
								...sensor,
								data: [[sensor.data[0][0], 0]],
							}
						}

					// Check if this sensor's ID matches any ID from powerProducers
					const producer = powerProducers.find((p) => p.kwSensorId === sensor.id)
					if (producer !== undefined) {
						return {
							...sensor,
							data: sensor.data.map(([date, value]) => [
								date,
								producer.maxKw !== null ? (100 / producer.maxKw) * value : 0,
							]),
						}
					} else {
						// If no match, return the sensor data unchanged
						return sensor
					}
				}),
			}
			if (vesselTrack !== undefined) {
				timeseriesDataWithPowerPercent.sensors.push({
					id: speedId,
					data: vesselTrack.locations
						.filter(
							(
								l
							): l is {
								timestamp: Date
								heading: number | null
								latitude: number
								longitude: number
								course: number | null
								speed: number
							} => l.speed !== null
						)
						.map((l) => [l.timestamp.getTime(), l.speed]),
				})
			}
			setTimeseriesDataV2(timeseriesDataWithPowerPercent)
		}
	}, [isTimeseriesDataLoading, timeseriesData, t1EventRange, powerProducers, vesselTrack])

	//find pairs of powerproducers and fuel consumers
	const [sensorPair, setSensorPair] = useState<Array<[number, number]>>([])
	useEffect(() => {
		if (eventData === null || eventData === undefined) {
			return
		}
		const newPairs = powerProducers.flatMap((producer): [number, number][] => {
			const tempLabel = common.sensorDataFromIds
				.get(producer.kwSensorId)
				?.display_name.split(" ")[0]

			const tempSensorID = producer.kwSensorId

			const matchedSensorId = fuelConsumerSensorIds.find(
				(id) => common.sensorDataFromIds.get(id)?.display_name.split(" ")[0] === tempLabel
			)

			if (matchedSensorId != null) {
				return [[matchedSensorId, tempSensorID] as [number, number]]
			} else {
				return []
			}
		})

		setSensorPair(newPairs)
	}, [eventData, fuelConsumerSensorIds, powerProducers])

	const baselineFuelRateIds = [-1, -2, -4, -6, -7] as const
	const baselinePowerProductionId = [-3] as const

	const [timeseriesDataWithBaseline, setTimeseriesDataWithBaseline] = useState<{
		sensors: {
			data: [number, number][]
			id: number
		}[]
	}>()

	useEffect(() => {
		if (
			isTimeseriesDataLoading === true ||
			timeseriesData === undefined ||
			timeseriesData === null ||
			timeseriesDataV2 === undefined ||
			timeseriesDataV2 === null
		) {
			return
		}

		const res = timeseriesDataV2.sensors.slice()

		if (
			isBaselineDataLoading === false &&
			baselineData !== undefined &&
			baselineData !== null &&
			baselineData.samples.length > 0
		) {
			const actualValueSensorData: SensorData = {
				id: baselineFuelRateIds[0],
				data: new Array<[number, number]>(),
			}

			const baselineValueSensorData: SensorData = {
				id: baselineFuelRateIds[1],
				data: new Array<[number, number]>(),
			}

			const powerProductionPercentageData: SensorData = {
				id: baselinePowerProductionId[0],
				data: new Array<[number, number]>(),
			}
			const baselineDiffPercentage: SensorData = {
				id: baselineFuelRateIds[2],
				data: new Array<[number, number]>(),
			}
			const totalEngineFuelRate: SensorData = {
				id: baselineFuelRateIds[3],
				data: new Array<[number, number]>(),
			}
			const totalVesselFuelRate: SensorData = {
				id: baselineFuelRateIds[4],
				data: new Array<[number, number]>(),
			}

			baselineData.samples.forEach((element) => {
				const timestamp = element.timestamp

				if (
					element.fuelRateLitresPerHour.baselineValue !== undefined ||
					element.fuelRateLitresPerHour.baselineValue != null ||
					element.fuelRateLitresPerHour.actualValue !== undefined ||
					element.fuelRateLitresPerHour.actualValue != null ||
					element.fuelRateLitresPerHour.engineTotal !== undefined ||
					element.fuelRateLitresPerHour.engineTotal != null ||
					element.fuelRateLitresPerHour.vesselTotal !== undefined ||
					element.fuelRateLitresPerHour.vesselTotal != null ||
					element.powerProductionPercentage !== undefined ||
					element.powerProductionPercentage != null
				) {
					actualValueSensorData.data.push([
						timestamp.getTime(),
						element.fuelRateLitresPerHour.actualValue ?? null,
					])
					baselineValueSensorData.data.push([
						timestamp.getTime(),
						element.fuelRateLitresPerHour.baselineValue ?? null,
					])
					powerProductionPercentageData.data.push([
						timestamp.getTime(),
						element.powerProductionPercentage ?? null,
					])
					if (
						element.fuelRateLitresPerHour.baselineValue !== undefined &&
						element.fuelRateLitresPerHour.actualValue !== undefined
					) {
						baselineDiffPercentage.data.push([
							timestamp.getTime(),
							element.fuelRateLitresPerHour.baselineValue === undefined
								? null
								: (100 * element.fuelRateLitresPerHour.actualValue) /
										element.fuelRateLitresPerHour.baselineValue -
									100,
						])
					}

					totalEngineFuelRate.data.push([
						timestamp.getTime(),
						element.fuelRateLitresPerHour.engineTotal ?? null,
					])

					totalVesselFuelRate.data.push([
						timestamp.getTime(),
						element.fuelRateLitresPerHour.vesselTotal ?? null,
					])
				}
			})

			const fixedActual = new Array<[number, number]>()
			for (const sample of actualValueSensorData.data) {
				const [x1, x2] = sample
				if (x2 == null) {
					continue
				}
				fixedActual.push([x1, x2])
			}
			let exists = res.some((element) => element.id === actualValueSensorData.id)
			if (fixedActual.some((element) => typeof element[1] === "number")) {
				if (!exists) {
					res.push({ id: actualValueSensorData.id, data: fixedActual })
				}
			}

			const fixedBaseline = new Array<[number, number]>()
			for (const sample of baselineValueSensorData.data) {
				const [x1, x2] = sample
				if (x2 == null) {
					continue
				}
				fixedBaseline.push([x1, x2])
			}
			if (fixedBaseline.some((element) => typeof element[1] === "number")) {
				exists = res.some((element) => element.id === baselineValueSensorData.id)
				if (!exists) {
					res.push({ id: baselineValueSensorData.id, data: fixedBaseline })
				}
			}

			const fixedPowerProductionPercentage = new Array<[number, number]>()
			for (const sample of powerProductionPercentageData.data) {
				const [x1, x2] = sample
				if (x2 == null) {
					continue
				}
				fixedPowerProductionPercentage.push([x1, x2])
			}
			if (fixedPowerProductionPercentage.some((element) => typeof element[1] === "number")) {
				exists = res.some((element) => element.id === powerProductionPercentageData.id)
				if (!exists) {
					res.push({
						id: powerProductionPercentageData.id,
						data: fixedPowerProductionPercentage,
					})
				}
			}
			const fixedTotalEngineFuelRate = new Array<[number, number]>()
			for (const sample of totalEngineFuelRate.data) {
				const [x1, x2] = sample
				if (x2 == null) {
					continue
				}
				fixedTotalEngineFuelRate.push([x1, x2])
			}
			if (fixedTotalEngineFuelRate.some((element) => typeof element[1] === "number")) {
				exists = res.some((element) => element.id === totalEngineFuelRate.id)
				if (!exists) {
					res.push({
						id: totalEngineFuelRate.id,
						data: fixedTotalEngineFuelRate,
					})
				}
			}
			const fixedTotalVesselFuelRate = new Array<[number, number]>()
			for (const sample of totalVesselFuelRate.data) {
				const [x1, x2] = sample
				if (x2 == null) {
					continue
				}
				fixedTotalVesselFuelRate.push([x1, x2])
			}
			if (fixedTotalVesselFuelRate.some((element) => typeof element[1] === "number")) {
				exists = res.some((element) => element.id === totalVesselFuelRate.id)
				if (!exists) {
					res.push({
						id: totalVesselFuelRate.id,
						data: fixedTotalVesselFuelRate,
					})
				}
			}

			const fixedDiffPercentage = new Array<[number, number]>()
			for (const sample of baselineDiffPercentage.data) {
				const [x1, x2] = sample
				if (x2 == null) {
					continue
				}
				fixedDiffPercentage.push([x1, x2])
			}

			if (fixedDiffPercentage.some((element) => typeof element[1] === "number")) {
				exists = res.some((element) => element.id === baselineDiffPercentage.id)
				if (!exists) {
					res.push({
						id: baselineDiffPercentage.id,
						data: fixedDiffPercentage,
					})
				}
			}
		}

		setTimeseriesDataWithBaseline({ sensors: res })
	}, [
		isTimeseriesDataLoading,
		timeseriesData,
		timeseriesDataV2,
		isBaselineDataLoading,
		baselineData,
	])

	// Baseline-derived virtual sensors are optional; the regular plots can render from
	// timeseriesDataV2 alone when no baseline exists for the vessel.

	const {
		isLoading: isTimeseriesWeatherDataLoading,
		isError: isTimeseriesWeatherDataError,
		data: timeseriesWeatherData,
	} = useGetTimeseries4Query({
		gte: t1EventRange,
		lt: t2EventRange,
		sensorIds: weatherSensorIds,
	})

	const opModeColorsMap = opModeIds

	const [compassWeatherData, setCompasWeatherData] = useState<CompassTimeseriesData[]>()

	useEffect(() => {
		if (
			timeseriesWeatherData === undefined ||
			timeseriesWeatherData === null ||
			timeseriesDataWithBaseline === undefined ||
			timeseriesDataWithBaseline === null ||
			opModesList === undefined
		) {
			return
		}

		let minProducerLoadTemp = 1000000000
		let minFuelTemp = 1000000000
		let maxFuelTemp = 0
		let minLoadTemp = 1000000000
		let minBaselineTemp = 1000000000

		const tempConditionPlotData: ChartData<"line"> = {
			labels: [],
			datasets: [],
		}
		const tempLoadPlotData: ChartData<"line"> = {
			labels: [],
			datasets: [],
		}
		const tempFuelPlotData: ChartData<"line"> = {
			labels: [],
			datasets: [],
		}

		const tempBaselinePlotData: ChartData<"line"> = {
			labels: [],
			datasets: [],
		}
		const initTs = t1EventRange.getTime()
		const endTs = t2EventRange.getTime()

		// fixes first and last timestamps for timeseriesV3 response
		for (const sensorData of timeseriesDataWithBaseline.sensors) {
			const tsData = sensorData.data
			const tsData0 = tsData?.[0]

			if (tsData.length === 1 && tsData0 !== undefined) {
				tsData0[0] = initTs
				tsData.push([endTs, tsData0[1]])
				continue
			}
			const tsDataLast = tsData?.[tsData.length - 1]
			if (tsData0 !== undefined) {
				const ts0 = tsData0[0]
				if (ts0 < initTs) {
					tsData0[0] = initTs
				}
			}
			if (tsDataLast !== undefined) {
				const tsLast = tsDataLast[0]
				if (tsLast < endTs) {
					sensorData.data.push([endTs, tsDataLast[1]])
				}
			}
		}

		// make signals uniform
		const uniformTable: {
			timestamp: Date
			sensorValues: Map<number, number | null>
		}[] = []

		// Group data by timestamp
		const dataGroupedByTimestamp = new Map<number, { sensorId: number; value: number }[]>()

		const sensorValueBySensorId = new Map<number, number | null>()

		for (const signal of timeseriesDataWithBaseline.sensors) {
			const sensorId = signal.id
			const data = signal.data
			for (const d of data) {
				const key = new Date(d[0]).setUTCSeconds(0, 0)
				let timestampDatapoints = dataGroupedByTimestamp.get(key)
				if (timestampDatapoints == undefined) {
					timestampDatapoints = []
					dataGroupedByTimestamp.set(key, timestampDatapoints)
				}
				timestampDatapoints.push({ sensorId, value: d[1] })
			}
		}

		// Run through the timestamps chronologically
		const t1Filter = t1EventRange.getTime()

		const timestampsSorted = Array.from(dataGroupedByTimestamp.keys())
			.sort((a, b) => a - b)
			.filter((f) => f >= t1Filter)

		for (const timestampMs of timestampsSorted) {
			const datapointsForTimestamp = dataGroupedByTimestamp.get(timestampMs) ?? []

			// Set the state of each sensor
			for (const datapoint of datapointsForTimestamp) {
				sensorValueBySensorId.set(datapoint.sensorId, datapoint.value)
			}
			// Create and save the row
			const row: {
				timestamp: Date
				sensorValues: Map<number, number | null>
			} = {
				sensorValues: new Map(),
				timestamp: new Date(timestampMs),
			}
			// For each sensor, there will be either a new value or a value that was previously set
			for (const [plantId, sensorValue] of sensorValueBySensorId) {
				row.sensorValues.set(plantId, sensorValue)
			}
			uniformTable.push(row)
		}

		const timeseriesDataUniform: {
			sensors: SensorData[]
		} = {
			sensors: timeseriesDataWithBaseline.sensors.map((signal) => {
				return {
					...signal,
					data: uniformTable
						.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
						.map((entry) => {
							return [
								entry.timestamp.getTime(),
								entry.sensorValues.get(signal.id) ?? null,
							]
						}),
				}
			}),
		}

		// to be removed when hybrid propulsion power and propulsion power is merged
		const conditionSensorsId = [
			...conditionSensorIds.filter((id) => id !== hybridPropulsionPowerID),
			speedId,
		]

		const conditionUnitsObj: { label: string; unit: string }[] = []
		conditionSensorsId.forEach((sensorId) => {
			const sensorData = common.sensorDataFromIds.get(sensorId)
			if (sensorId === speedId) {
				conditionUnitsObj.push({ label: t("VESSEL SPEED"), unit: "knots" })
			} else if (sensorData !== undefined) {
				conditionUnitsObj.push({ label: sensorData.display_name, unit: sensorData.unit })
			}
		})

		for (const tsd of timeseriesDataUniform.sensors) {
			for (const index of baselinePowerProductionId) {
				if (tsd.id === index) {
					conditionUnitsObj.push({ label: t("POWER PRODUCTION"), unit: "%" })
				}
			}
		}

		let conditionLinePlotOptionsTemp: ChartOptions<"line"> =
			conditionLinePlotOptions === undefined
				? {
						// responsive: true,
						maintainAspectRatio: false,
						interaction: {
							mode: "index",
							intersect: false,
						},
						plugins: {
							tooltip: {
								// animation: {
								// 	duration: 400, // ✅ Tooltip animation enabled
								// },
								callbacks: {
									label(tooltipItem) {
										const label = tooltipItem.dataset.label
										const value = tooltipItem.parsed.y
										let tempActualProductionKW: number | undefined = undefined
										let tempActualPropulsionKW: number | undefined = undefined
										const normalizedLabel = label?.toLowerCase() ?? ""
										const maxPropulsionKW = plantsMaxKw?.find(
											(f) => f.plantId === plantData?.id
										)?.maxKw

										if (
											normalizedLabel.includes("production") == true &&
											plantMaxPowerProducedKw !== undefined
										) {
											tempActualProductionKW =
												(plantMaxPowerProducedKw / 100) * value
										}

										if (
											normalizedLabel.includes("propulsion") == true &&
											normalizedLabel.includes("hybrid") == false &&
											maxPropulsionKW !== undefined
										) {
											tempActualPropulsionKW = (maxPropulsionKW / 100) * value
										}

										return `${label}: ${
											typeof value === "number" ? value.toFixed(1) : value
										} ${
											conditionUnitsObj?.find((u) => u.label === label)
												?.unit ??
											`${label?.includes("Speed") == true ? `kn` : ""}`
										} ${
											tempActualPropulsionKW !== undefined
												? `/ ${tempActualPropulsionKW.toFixed(0)} KW`
												: ""
										}${
											tempActualProductionKW !== undefined
												? `/ ${tempActualProductionKW.toFixed(0)} KW`
												: ""
										}
										`
									},
									afterBody(tooltipItems) {
										if (canUserSeeAquacultureColumns === false) {
											return []
										}
										const timestampMs = Number(tooltipItems?.[0]?.parsed?.x)
										const activeFishModes = getFishModesAtTimestamp(
											fishLogEventsNormalized,
											timestampMs
										)
										if (activeFishModes.length === 0) {
											return []
										}
										return [`${t("Fish mode")}: ${activeFishModes.join(", ")}`]
									},
								},
								intersect: false,
								mode: "index",
							},
							legend: {
								position: "bottom",
							},
							title: {
								display: false,
								text: ``,
							},
							zoom: {
								limits: {
									x: {
										min: Math.min(
											...eventData.map((e) => new Date(e.event.t1).getTime())
										),
										max: Math.max(
											...eventData.map((e) => new Date(e.event.t2).getTime())
										),
									},
								},
								pan: {
									enabled: true,
									mode: "x",
									onPan: function () {
										const chartData = conditionChartRef.current
										if (chartData === null) {
											return
										}
										const xScale = chartData.scales.x
										if (xScale === undefined) {
											return
										}
										leftEnd = xScale.min
										rightEnd = xScale.max
										updateChartTimeRange([
											conditionChartRef.current,
											fuelChartRef.current,
											baselineChartRef.current,
											loadChartRef.current,
										])
									},
								},
								zoom: {
									wheel: {
										enabled: true,
									},
									pinch: {
										enabled: true,
									},
									onZoom: function () {
										const chartData = conditionChartRef.current
										if (chartData === null) {
											return
										}
										const xScale = chartData.scales.x
										if (xScale === undefined) {
											return
										}
										leftEnd = xScale.min
										rightEnd = xScale.max
										updateChartTimeRange([
											conditionChartRef.current,
											fuelChartRef.current,
											baselineChartRef.current,
											loadChartRef.current,
										])
									},
									mode: "x",
								},
							},
							bgColor: {
								colorMap: {},
								ranges: [],
							},
							crosshairV2: {
								timestampGetter: timestampGetter,
								sync: {
									enabled: true,
									group:
										Math.min(
											...eventData.map((e) => new Date(e.event.t1).getTime())
										) +
										Math.max(
											...eventData.map((e) => new Date(e.event.t2).getTime())
										),
									suppressTooltips: false,
								},
							},
						},
						scales: {
							x: {
								type: "time",
								time: {
									displayFormats: {
										hour: "HH:mm",
										minute: "HH:mm",
										second: "HH:mm:ss",
									},
									tooltipFormat: "MMM dd, yyyy, HH:mm:ss",
								},
								title: {
									display: false,
									text: "time",
								},
							},
						},
					}
				: conditionLinePlotOptions
		conditionLinePlotOptionsTemp = {
			...conditionLinePlotOptionsTemp,
			plugins: {
				...conditionLinePlotOptionsTemp.plugins,
				tooltip: {
					...conditionLinePlotOptionsTemp.plugins?.tooltip,
					callbacks: {
						...conditionLinePlotOptionsTemp.plugins?.tooltip?.callbacks,
						label(tooltipItem) {
							const label = tooltipItem.dataset.label
							const value = tooltipItem.parsed.y
							let tempActualProductionKW: number | undefined = undefined
							let tempActualPropulsionKW: number | undefined = undefined
							const normalizedLabel = label?.toLowerCase() ?? ""
							const maxPropulsionKW = plantsMaxKw?.find(
								(f) => f.plantId === plantData?.id
							)?.maxKw

							if (
								normalizedLabel.includes("production") == true &&
								plantMaxPowerProducedKw !== undefined
							) {
								tempActualProductionKW = (plantMaxPowerProducedKw / 100) * value
							}

							if (
								normalizedLabel.includes("propulsion") == true &&
								normalizedLabel.includes("hybrid") == false &&
								maxPropulsionKW !== undefined
							) {
								tempActualPropulsionKW = (maxPropulsionKW / 100) * value
							}

							return `${label}: ${
								typeof value === "number" ? value.toFixed(1) : value
							} ${conditionUnitsObj?.find((u) => u.label === label)?.unit ?? ""} ${
								tempActualPropulsionKW !== undefined
									? `/ ${tempActualPropulsionKW.toFixed(0)} KW`
									: ""
							}${
								tempActualProductionKW !== undefined
									? `/ ${tempActualProductionKW.toFixed(0)} KW`
									: ""
							}
										`
						},
					},
				},
			},
		}

		const conditionUnits: string[] = []
		let conditionColorIndex = 0
		if (conditionSensorsId !== undefined) {
			for (const [index, sensorId] of conditionSensorsId.entries()) {
				const sensorData: SensorData[] = []
				for (const tsd of timeseriesDataUniform.sensors) {
					if (tsd.id === sensorId) {
						sensorData.push(tsd)
					}
				}

				if (sensorData.length > 0) {
					if (hasConditionBgRanges) {
						conditionLinePlotOptionsTemp = {
							...conditionLinePlotOptionsTemp,
							plugins: {
								...conditionLinePlotOptionsTemp.plugins,
								bgColor: showOpModeBg === true ? conditionBgColors : {},
							},
						}
					}

					const conditionUnit =
						sensorId === speedId
							? "knots"
							: common.sensorDataFromIds.get(sensorId)?.unit

					if (
						conditionUnit !== undefined &&
						((conditionLinePlotOptionsTemp.scales !== undefined &&
							Object.keys(conditionLinePlotOptionsTemp.scales).length === 1) ||
							conditionUnits.includes(conditionUnit) === false)
					) {
						conditionUnits.push(conditionUnit)
						conditionLinePlotOptionsTemp = {
							...conditionLinePlotOptionsTemp,
							scales: {
								...conditionLinePlotOptionsTemp.scales,
								[conditionUnits.length - 1 === 0
									? "y"
									: `y${conditionUnits.length - 1}`]: {
									title: {
										display: true,
										text: `${conditionUnit}`,
									},
									ticks: {
										precision: 1,
									},
									grid: { display: index === 0 ? true : false },
								},
							},
						}
					}

					tempConditionPlotData.labels = sensorData
						.map((sd) => sd.data.map((dp) => dp[0]))
						.flat()
					const unitIndex = conditionUnits.findIndex((u) => u === conditionUnit)

					tempConditionPlotData.datasets.push({
						label:
							sensorId === speedId
								? t("VESSEL SPEED")
								: (common.sensorDataFromIds.get(sensorId)?.display_name ??
									t("UNKNOWN SENSOR")),
						data: sensorData.map((sd) => sd.data.map((dp) => dp[1])).flat(),
						borderColor: COLORS_PLOTS_COLORBLIND[conditionColorIndex],
						backgroundColor: COLORS_PLOTS_COLORBLIND[conditionColorIndex],
						borderWidth: 1,
						showLine: true,
						animation: false, //helps with rendering performance
						pointRadius: 0, //helps with rendering performance
						spanGaps: true, //helps with rendering performance
						yAxisID: unitIndex === 0 ? "y" : `y${unitIndex}`,
					})

					conditionColorIndex = conditionColorIndex + 1
				}
			}
		}

		if (baselineData?.samples[0]?.powerProductionPercentage !== undefined) {
			for (const index of baselinePowerProductionId) {
				const sensorData: SensorData[] = []
				for (const tsd of timeseriesDataUniform.sensors) {
					if (tsd.id === index) {
						sensorData.push(tsd)
					}
				}

				if (sensorData.length > 0) {
					if (hasConditionBgRanges) {
						conditionLinePlotOptionsTemp = {
							...conditionLinePlotOptionsTemp,
							plugins: {
								...conditionLinePlotOptionsTemp.plugins,
								bgColor: showOpModeBg === true ? conditionBgColors : {},
							},
						}
					}

					const conditionUnit = "%"
					if (
						conditionUnit !== undefined &&
						conditionUnits.includes(conditionUnit) === false
					) {
						conditionUnits.push(conditionUnit)
						conditionLinePlotOptionsTemp = {
							...conditionLinePlotOptionsTemp,
							scales: {
								...conditionLinePlotOptionsTemp.scales,
								[conditionUnits.length - 1 === 0
									? "y"
									: `y${conditionUnits.length - 1}`]: {
									title: {
										display: true,
										text: `${conditionUnit}`,
									},
									ticks: {
										precision: 1,
									},
									grid: { display: false },
								},
							},
						}
					}

					const unitIndex = conditionUnits.findIndex((u) => u === conditionUnit)

					tempConditionPlotData.datasets.push({
						label: t("POWER PRODUCTION"),
						data: sensorData.map((sd) => sd.data.map((dp) => dp[1])).flat(),
						borderColor: COLORS_PLOTS_COLORBLIND[conditionColorIndex],
						backgroundColor: COLORS_PLOTS_COLORBLIND[conditionColorIndex],
						borderWidth: 1,
						showLine: true,
						animation: false, //helps with rendering performance
						pointRadius: 0, //helps with rendering performance
						spanGaps: true, //helps with rendering performance
						yAxisID: unitIndex === 0 ? "y" : `y${unitIndex}`,
					})
				}
			}
		}

		const loadSensorsId = loadSensorIds
		const loadLinePlotOptionsTemp = loadLinePlotOptions

		//combine others

		if (loadSensorsId !== undefined) {
			const powerConsumed = timeseriesDataUniform.sensors.filter((f) =>
				loadSensorsId?.includes(f.id)
			)
			const totalPowerProducedSensor = timeseriesDataUniform.sensors.find((f) => f.id === -3)

			const totalPowerProduced: SensorData | undefined =
				totalPowerProducedSensor !== undefined
					? {
							id: totalPowerProducedSensor.id,
							data: totalPowerProducedSensor.data.map(([date, powerPercent]) => {
								if (
									powerPercent !== null &&
									plantMaxPowerProducedKw !== undefined
								) {
									return [
										date,
										(plantMaxPowerProducedKw / 100) * powerPercent,
									] as [number, number]
								} else {
									return [date, null] as [number, number | null]
								}
							}),
						}
					: undefined

			const dateMap = new Map<string, number | null>()

			powerConsumed.forEach((sensor) => {
				sensor.data.forEach(([date, value]) => {
					const dateString = new Date(date).toISOString()
					const currentSum = dateMap.get(dateString)

					if (value === null) {
						// If value is null, keep the current sum as null if it's already null
						if (currentSum === undefined || currentSum === null) {
							dateMap.set(dateString, null)
						}
					} else {
						// If the current sum is null, set to current value, otherwise add the value
						if (currentSum === undefined) {
							dateMap.set(dateString, value)
						} else if (currentSum !== null) {
							dateMap.set(dateString, currentSum + value)
						}
					}
				})
			})

			totalPowerProduced?.data.forEach(([date, totalValue]) => {
				const dateString = new Date(date).toISOString()
				const sumValue = dateMap.get(dateString)

				if (totalValue === null || sumValue === null) {
					// If either total or sum is null, result is null
					dateMap.set(dateString, null)
				} else {
					// Subtract the sum from the total
					const resultValue = totalValue - (sumValue ?? 0)
					dateMap.set(dateString, resultValue)
				}
			})

			const combinedData: [number, number | null][] = Array.from(dateMap.entries()).map(
				([dateString, result]) => [new Date(dateString).getTime(), result]
			)
			const otherConsumers: SensorData = {
				id: 100, // Using a new ID or any other ID you prefer
				data: combinedData,
			}

			for (const [index, sensorId] of loadSensorsId.entries()) {
				const sensorData: SensorData[] = []

				for (const tsd of timeseriesDataUniform.sensors) {
					if (tsd.id === sensorId) {
						sensorData.push(tsd)
					}
				}

				if (sensorData.length > 0) {
					const isComponentStopped =
						sensorData.reduce(
							(acc, sd) =>
								acc +
								sd.data.reduce(
									(acc2, current2) =>
										acc2 + (current2[1] === null ? 0 : current2[1]),
									0
								),
							0
						) === 0

					if (isComponentStopped === false) {
						sensorData.forEach((sd) =>
							sd.data.forEach((dp) => {
								if (dp[1] !== null && dp[1] < minLoadTemp) {
									minLoadTemp = dp[1]
								}
							})
						)
						const pluginsRef = loadLinePlotOptionsTemp.plugins
						if (hasOperationModeEvents && pluginsRef !== undefined) {
							pluginsRef.bgColor = showOpModeBg === true ? operationModeBgColors : {}
						}

						tempLoadPlotData.labels = sensorData
							.map((sd) => sd.data.map((dp) => dp[0]))
							.flat()
						tempLoadPlotData.datasets.push({
							label:
								common.sensorDataFromIds.get(sensorId)?.display_name ??
								t("UNKNOWN SENSOR"),
							data: sensorData.map((sd) => sd.data.map((dp) => dp[1])).flat(),
							borderColor: COLORS_PLOTS_COLORBLIND[index],
							backgroundColor: COLORS_PLOTS_COLORBLIND[index],
							borderWidth: 1,
							showLine: true,
							animation: false, //helps with rendering performance
							pointRadius: 0, //helps with rendering performance
							spanGaps: true, //helps with rendering performance
						})
					} else {
						tempLoadPlotData.datasets.push({
							label: `${
								common.sensorDataFromIds
									.get(sensorId)
									?.display_name.split(" Power")[0]
							}: ${t("NOT RUNNING").toLowerCase()}`,
							data: sensorData.map((sd) => sd.data.map((dp) => null)).flat(),
							borderColor: "#aaa",
							backgroundColor: "#aaa",
							borderWidth: 1,
							showLine: true,
							animation: false, //helps with rendering performance
							pointRadius: 0, //helps with rendering performance
							spanGaps: true, //helps with rendering performance
						})
					}
				}
			}
			// if (hybridPropulsionPowerID === -1) {
			// 	tempLoadPlotData.datasets.push({
			// 		label: "Others (hotel, deck machinery, etc)",
			// 		data: otherConsumers.data.map((a) => a[1]),
			// 		borderColor: "#aaa",
			// 		backgroundColor: "#aaa",
			// 		borderWidth: 1,
			// 		showLine: true,
			// 		animation: false,
			// 		pointRadius: 0,
			// 		spanGaps: true,
			// 	})
			// }
		}

		setLoadLinePlotOptions({
			...loadLinePlotOptionsTemp,
			plugins: {
				...loadLinePlotOptionsTemp.plugins,
				tooltip: {
					...loadLinePlotOptions.plugins?.tooltip,
					// animation: {
					// 	duration: 400, // ✅ Tooltip animation enabled
					// },
					callbacks: {
						label(tooltipItem) {
							const label =
								tooltipItem.dataset.label?.includes("Other") === true
									? "Other"
									: tooltipItem.dataset.label
							const value = tooltipItem.parsed.y

							return `${label}: ${
								typeof value === "number" ? value.toFixed(1) : value
							} kW`
						},
					},
				},
				legend: {
					...loadLinePlotOptionsTemp.plugins?.legend,
					labels: {
						generateLabels(chart) {
							const labelsOriginal =
								ChartJS.defaults.plugins.legend.labels.generateLabels(chart)

							if (labelsOriginal !== undefined) {
								labelsOriginal.forEach((label) => {
									if (label.text.includes("running") === true) {
										label.fontColor = "#bbb"
									}
								})
							}
							return labelsOriginal
						},
					},
				},
			},
		})
		const fuelConsumersSensorsId = uniqueNumbers([
			...powerProducerKwSensorIds,
			...fuelConsumerSensorIds,
		])
		let fuelConsumerColorIndex = 0
		if (fuelConsumersSensorsId !== undefined) {
			for (const [index, sensorId] of fuelConsumersSensorsId.entries()) {
				const sensorData: SensorData[] = []
				for (const tsd of timeseriesDataUniform.sensors) {
					if (tsd.id === sensorId) {
						sensorData.push(tsd)
					}
				}

				if (sensorData.length > 0) {
					const producer = powerProducers.find((p) => p.kwSensorId === sensorId)
					if (producer?.maxKw === null) {
						const sensorDisplayName =
							common.sensorDataFromIds.get(sensorId)?.display_name ??
							t("UNKNOWN SENSOR")
						tempFuelPlotData.datasets.push({
							label: `${sensorDisplayName.split(" Power")[0]}: ${t("MAX POWER NOT CONFIGURED").toLowerCase()}`,
							data: sensorData
								.map((sd) => sd.data.map((dp) => dp[1]))
								.flat()
								.map((d) => null),
							borderColor: "#aaa",
							backgroundColor: "#aaa",
							borderWidth: 1,
							showLine: true,
							animation: false, //helps with rendering performance
							pointRadius: 0, //helps with rendering performance
							spanGaps: true, //helps with rendering performance
						})
						continue
					}

					const isComponentRunning =
						sensorData.reduce(
							(acc, sd) =>
								acc +
								sd.data.reduce(
									(acc2, current2) =>
										acc2 + (current2[1] === null ? 0 : current2[1]),
									0
								),
							0
						) > 0

					if (isComponentRunning === true) {
						if (sensorPair.find((a) => a[0] === sensorId) !== undefined) {
							sensorData.forEach((sd) =>
								sd.data.forEach((dp) => {
									if (dp[1] !== null && dp[1] > maxFuelTemp) {
										maxFuelTemp = dp[1]
									}
								})
							)

							sensorData.forEach((sd) =>
								sd.data.forEach((dp) => {
									if (dp[1] !== null && dp[1] < minFuelTemp) {
										minFuelTemp = dp[1]
									}
								})
							)
						} else {
							sensorData.forEach((sd) =>
								sd.data.forEach((dp) => {
									if (dp[1] !== null && dp[1] < minProducerLoadTemp) {
										minProducerLoadTemp = dp[1] < 0 ? 0 : dp[1]
									}
								})
							)
						}

						setFuelLinePlotOptions({
							...fuelLinePlotOptions,
							plugins: {
								...fuelLinePlotOptions.plugins,
								legend: {
									display: true,
									labels: {
										filter: function (item, chart) {
											const itemTextSplit_1 = item.text.split(" ")?.[1]
											if (
												itemTextSplit_1 !== undefined &&
												itemTextSplit_1.includes("Fuel")
											) {
												return false
											}

											return true // Return true to show label
										},
									},
									position: "bottom",
								},
								bgColor:
									showOpModeBg === true && hasOperationModeEvents
										? operationModeBgColors
										: {},
							},
						})

						const flattnedValues = sensorData
							.map((sd) => sd.data.map((dp) => dp[1]))
							.flat()

						if (
							common.sensorDataFromIds
								.get(sensorId)
								?.display_name.includes("Fuel") === false
								? (flattnedValues as number[]).reduce(
										(acc, val) => (val > acc ? val : acc),
										-Infinity
									) > THRESHOLD_POWER
									? true
									: false
								: true
						) {
							tempFuelPlotData.labels = sensorData
								.map((sd) => sd.data.map((dp) => dp[0]))
								.flat()

							tempFuelPlotData.datasets.push({
								label:
									common.sensorDataFromIds.get(sensorId)?.display_name ??
									t("UNKNOWN SENSOR"),
								data: sensorData
									.map((sd) => sd.data.map((dp) => dp[1]))
									.flat()
									.map((d) => (d !== null && d < THRESHOLD_POWER ? 0 : d)),
								borderColor: COLORS_PLOTS_COLORBLIND[fuelConsumerColorIndex],
								backgroundColor: COLORS_PLOTS_COLORBLIND[fuelConsumerColorIndex],
								borderWidth: 1,
								showLine: true,
								animation: false, //helps with rendering performance
								pointRadius: 0, //helps with rendering performance
								spanGaps: true, //helps with rendering performance
								hidden:
									(common.sensorDataFromIds
										.get(sensorId)
										?.display_name.includes("Fuel") ?? false)
										? true
										: false,
							})

							fuelConsumerColorIndex += 1
						} else {
							if (
								common.sensorDataFromIds
									.get(sensorId)
									?.display_name.includes("Power") === true
							) {
								tempFuelPlotData.datasets.push({
									label: `${
										common.sensorDataFromIds
											.get(sensorId)
											?.display_name.split(" Power")[0]
									}: ${t("NOT RUNNING").toLowerCase()}`,
									data: sensorData
										.map((sd) => sd.data.map((dp) => dp[1]))
										.flat()
										.map((d) => null),
									borderColor: "#aaa",
									backgroundColor: "#aaa",
									borderWidth: 1,
									showLine: true,
									animation: false, //helps with rendering performance
									pointRadius: 0, //helps with rendering performance
									spanGaps: true, //helps with rendering performance
								})
							}
						}
					} else {
						if (
							common.sensorDataFromIds
								.get(sensorId)
								?.display_name.includes("Power") === true
						) {
							tempFuelPlotData.datasets.push({
								label: `${
									common.sensorDataFromIds
										.get(sensorId)
										?.display_name.split(" Power")[0]
								}: ${t("NOT RUNNING").toLowerCase()}`,
								data: sensorData
									.map((sd) => sd.data.map((dp) => dp[1]))
									.flat()
									.map((d) => null),
								borderColor: "#aaa",
								backgroundColor: "#aaa",
								borderWidth: 1,
								showLine: true,
								animation: false, //helps with rendering performance
								pointRadius: 0, //helps with rendering performance
								spanGaps: true, //helps with rendering performance
							})
						}
					}
				}
			}
		}

		setFuelLinePlotOptions({
			...fuelLinePlotOptions,
			plugins: {
				...fuelLinePlotOptions.plugins,
				legend: {
					display: true,
					labels: {
						generateLabels(chart) {
							const labelsOriginal =
								ChartJS.defaults.plugins.legend.labels.generateLabels(chart)

							if (labelsOriginal !== undefined) {
								labelsOriginal.forEach((label) => {
									if (label.text.includes("running") === true) {
										label.fontColor = "#bbb"
									}
								})
							}
							return labelsOriginal
						},
						filter: function (item, chart) {
							const itemTextSplit_1 = item.text.split(" ")?.[1]
							if (itemTextSplit_1 !== undefined && itemTextSplit_1.includes("Fuel")) {
								return false
							}
							return true // Return true to show label
						},
					},
					position: "bottom",
				},

				tooltip: {
					// animation: {
					// 	duration: 400, // ✅ Tooltip animation enabled
					// },
					mode: "index",
					intersect: false,
					callbacks: {
						label: function (context) {
							if (context === undefined) {
								return
							}

							const label = context.dataset.label
							if (label === undefined) {
								return
							}
							const shortLabel = label.split(" ")[0]
							// Get all matched sensor indexes
							const matchedSensorIndexes = context.chart.data.datasets.flatMap(
								(dataset, index) =>
									dataset.label?.split(" ")[0] === shortLabel ? index : []
							)
							const labelSplit_1 = label.split(" ")?.[1]
							if (labelSplit_1 !== undefined && labelSplit_1.includes("Fuel")) {
								return ""
							}

							// Prepare for accumulating all fuel rates
							let fuelRatesCombined = `/ ${t("FUEL RATE")}`

							// Iterate over all matched indexes to combine fuel rates
							matchedSensorIndexes.forEach((matchedIndex) => {
								const dataSet = context.chart.data.datasets[matchedIndex]
								if (dataSet !== undefined) {
									const otherValue = dataSet.data[context.dataIndex]
									const label = dataSet.label ?? ""
									if (label.includes("Fuel")) {
										const tempLabel = dataSet.label?.split(" ")
										const otherValue = dataSet.data[context.dataIndex]
										const tempLabelParts = dataSet.label?.split(" ")
										const tempLabel2 = tempLabelParts?.slice(3)

										const fuelRate = `${tempLabel2?.reduce(
											(prev, curr) => `${prev} ${curr}`,
											""
										)}: ${
											Number.isNaN(otherValue) || otherValue == null
												? otherValue
												: (otherValue as number).toFixed(1)
										} L/h  `

										// Append this fuel rate to the combined string
										fuelRatesCombined += `${fuelRate}`
									}
								}
							})

							// Return the combined tooltip with load and all fuel rates
							return `${label.split(" ")[0]} Load: ${context.parsed.y.toFixed(
								1
							)} % ${fuelRatesCombined}`
						},
					},
				},
				bgColor:
					showOpModeBg === true && hasOperationModeEvents ? operationModeBgColors : {},
			},
		})

		const buildSummedFuelRateSensor = (
			id: (typeof baselineFuelRateIds)[3] | (typeof baselineFuelRateIds)[4],
			sensorIds: number[]
		): SensorData | undefined => {
			const sourceSensors = uniqueNumbers(sensorIds)
				.map((sensorId) =>
					timeseriesDataUniform.sensors.find((sensor) => sensor.id === sensorId)
				)
				.filter((sensor): sensor is SensorData => sensor !== undefined)
			const firstSensor = sourceSensors[0]
			if (firstSensor === undefined) {
				return undefined
			}

			const data = firstSensor.data.map(([timestamp], index): [number, number | null] => {
				let sum = 0
				let hasValue = false
				for (const sensor of sourceSensors) {
					const value = sensor.data[index]?.[1]
					if (value === null || value === undefined) {
						continue
					}
					sum += value
					hasValue = true
				}
				return [timestamp, hasValue ? sum : null]
			})

			return data.some(([, value]) => typeof value === "number") ? { id, data } : undefined
		}

		const fallbackTotalEngineFuelRate = buildSummedFuelRateSensor(
			baselineFuelRateIds[3],
			sensorPair.map(([fuelSensorId]) => fuelSensorId)
		)
		if (
			fallbackTotalEngineFuelRate !== undefined &&
			timeseriesDataUniform.sensors.some(
				(sensor) => sensor.id === fallbackTotalEngineFuelRate.id
			) === false
		) {
			timeseriesDataUniform.sensors.push(fallbackTotalEngineFuelRate)
		}

		const fallbackTotalVesselFuelRate = buildSummedFuelRateSensor(
			baselineFuelRateIds[4],
			fuelConsumerSensorIds
		)
		if (
			fallbackTotalVesselFuelRate !== undefined &&
			timeseriesDataUniform.sensors.some(
				(sensor) => sensor.id === fallbackTotalVesselFuelRate.id
			) === false
		) {
			timeseriesDataUniform.sensors.push(fallbackTotalVesselFuelRate)
		}

		let baselineColorIndex = 0

		if (timeseriesDataUniform.sensors.some((sensor) => [-1, -2, -6, -7].includes(sensor.id))) {
			// Actual/baseline values are model-dependent, while total engine/vessel fuel
			// rates can still be plotted when no baseline config exists.
			const ids = [-1, -2, -6, -7]

			// Compare engine fuel rate and vessel fuel rate, hide engine fuel rate from plot if both data are the same
			let baselineActualFuelRate: number[] | undefined = undefined
			let engineFuelRate: number[] | undefined = undefined
			let vesselFuelRate: number[] | undefined = undefined

			for (const id of ids) {
				const sensorData: SensorData[] = []
				for (const tsd of timeseriesDataUniform.sensors) {
					if (tsd.id === id) {
						sensorData.push(tsd)
					}
				}

				// Store -6 and -7 data for later comparison
				if (
					id === -1 &&
					sensorData.length > 0 &&
					sensorData[0] != null &&
					sensorData[0].data != null
				) {
					// Only include non-null values for comparison
					baselineActualFuelRate = sensorData[0].data
						.map((dp) => dp[1])
						.filter((v): v is number => v !== null && v !== undefined)
				}
				if (
					id === -6 &&
					sensorData.length > 0 &&
					sensorData[0] != null &&
					sensorData[0].data != null
				) {
					// Only include non-null values for comparison
					engineFuelRate = sensorData[0].data
						.map((dp) => dp[1])
						.filter((v): v is number => v !== null && v !== undefined)
				}
				if (
					id === -7 &&
					sensorData.length > 0 &&
					sensorData[0] != null &&
					sensorData[0].data != null
				) {
					vesselFuelRate = sensorData[0].data
						.map((dp) => dp[1])
						.filter((v): v is number => v !== null && v !== undefined)
				}
			}

			for (const id of ids) {
				// If id is -6 and both engineFuelRate and vesselFuelRate are defined and not null, compare
				if (id === -6 && Array.isArray(engineFuelRate) && Array.isArray(vesselFuelRate)) {
					const EPSILON = 1e-10
					const isSame =
						engineFuelRate.length === vesselFuelRate.length &&
						engineFuelRate.every(
							(v, i) =>
								typeof vesselFuelRate[i] === "number" &&
								Math.abs(v - vesselFuelRate[i]!) < EPSILON
						)
					if (isSame) continue
				}
				if (
					id === -7 &&
					Array.isArray(vesselFuelRate) &&
					Array.isArray(baselineActualFuelRate)
				) {
					const EPSILON = 1e-10
					const isSame =
						vesselFuelRate.length === baselineActualFuelRate.length &&
						vesselFuelRate.every(
							(v, i) =>
								typeof baselineActualFuelRate[i] === "number" &&
								Math.abs(v - baselineActualFuelRate[i]!) < EPSILON
						)
					if (isSame) continue
				}

				const sensorData: SensorData[] = []
				for (const tsd of timeseriesDataUniform.sensors) {
					if (tsd.id === id) {
						sensorData.push(tsd)
					}
				}
				if (sensorData.length > 0) {
					const isComponentRunning =
						sensorData.reduce(
							(acc, sd) =>
								acc +
								sd.data.reduce(
									(acc2, current2) =>
										acc2 + (current2[1] === null ? 0 : current2[1]),
									0
								),
							0
						) >= 0

					if (isComponentRunning === true) {
						sensorData.forEach((sd) =>
							sd.data.forEach((dp) => {
								if (dp[1] !== null && dp[1] < minBaselineTemp) {
									minBaselineTemp = dp[1]
								}
							})
						)

						if (hasOperationModeEvents) {
							setBaselinePlotOptions({
								...baselinePlotOptions,
								plugins: {
									...baselinePlotOptions.plugins,
									bgColor: showOpModeBg === true ? operationModeBgColors : {},
								},
							})
						}
						let tempName = t("UNKNOWN SENSOR")

						if (id === baselineFuelRateIds[0]) {
							tempName = `${baselineName}${t("ACTUAL")}${baselineUnit.includes("L/h") ? ` ${t("FUEL RATE").toLowerCase()}` : ""}`
						}
						if (id === baselineFuelRateIds[1]) {
							tempName = `${baselineName}${t("BASELINE")}${baselineUnit.includes("L/h") ? ` ${t("FUEL RATE").toLowerCase()}` : ""}`
						}
						if (id === baselineFuelRateIds[2]) {
							tempName = `${baselineName}${t("DEVIATION")}`
						}
						if (id === baselineFuelRateIds[3]) {
							tempName = t("TOTAL ENGINE FUEL RATE")
						}
						if (id === baselineFuelRateIds[4]) {
							tempName = t("TOTAL VESSEL FUEL RATE")
						}

						tempBaselinePlotData.labels = sensorData
							.map((sd) => sd.data.map((dp) => dp[0]))
							.flat()

						tempBaselinePlotData.datasets.push({
							label: tempName,
							data: sensorData
								.map((sd) => sd.data.map((dp) => dp[1]))
								.flat()
								.map((d) => d),
							borderColor: COLORS_PLOTS_COLORBLIND[baselineColorIndex],
							backgroundColor: `${COLORS_PLOTS_COLORBLIND[baselineColorIndex]}`,
							borderWidth: 1,
							showLine: true,
							animation: false, //helps with rendering performance
							pointRadius: 0, //helps with rendering performance
							spanGaps: true, //helps with rendering performance
							yAxisID: tempName.toLowerCase().includes("fuel rate") ? "y" : "y1",
						})
						baselineColorIndex += 1
					}
				}
			}
		}

		if (baselineData?.samples[0] !== undefined) {
			for (const id of [-4]) {
				const sensorData: SensorData[] = []
				for (const tsd of timeseriesDataUniform.sensors) {
					if (tsd.id === id) {
						sensorData.push(tsd)
					}
				}

				if (sensorData.length > 0) {
					const values = sensorData.map((a) => a.data.map((b) => b[1]))

					const values2: number[] = values.reduce<number[]>(
						(acc, cur) => [
							...acc,
							...cur.filter((number): number is number => number !== null),
						],
						[]
					)

					function roundToClosest10(num: number, isMin?: boolean): number {
						if (isMin == true) return Math.floor(num / 10) * 10
						return Math.ceil(num / 10) * 10
					}

					const min =
						roundToClosest10(Math.min(...values2), true) < -100
							? -100
							: roundToClosest10(Math.min(...values2), true)

					const max =
						roundToClosest10(Math.max(...values2)) > 100
							? 100
							: roundToClosest10(Math.max(...values2) + 0.1)

					setBaselinePlotOptions({
						...baselinePlotOptions,
						scales: {
							...baselinePlotOptions.scales,
							[`y${baselineUnit.includes("L/h") ? "1" : "2"}`]: {
								min: min,
								max: max,
								title: {
									display: true,
									text: t("BASELINE DEVIATION (%)"),
								},
								grid: { display: false },
							},
						},
						plugins: {
							...baselinePlotOptions.plugins,
							bgColor:
								showOpModeBg === true && hasOperationModeEvents
									? operationModeBgColors
									: {},
						},
					})

					let tempName = t("UNKNOWN SENSOR")

					if (id === baselineFuelRateIds[2]) {
						tempName = `${baselineName}${t("DEVIATION")}`
					}

					const events = eventData.map((a) => a.event)
					let baselineSelectorColor = `#166534`

					const baselines = events.map((a) => {
						const baseline =
							(100 * a.fuelActualM3) /
								(a.fuelBaselineM3 === null ? 0 : a.fuelBaselineM3) -
							100
						const duration = new Date(a.t2).getTime() - new Date(a.t1).getTime()
						return { baseline, duration }
					})

					const totalWeightedBaseline = baselines.reduce(
						(acc, cur) => acc + cur.baseline * cur.duration,
						0
					)
					const totalDuration = baselines.reduce((acc, cur) => acc + cur.duration, 0)
					const totalEventBaseline =
						totalDuration !== undefined ? totalWeightedBaseline / totalDuration : 0

					if (totalEventBaseline > 0) {
						baselineSelectorColor = `#f7b443`
					}
					if (totalEventBaseline > 5) {
						baselineSelectorColor = `#ef4444`
					}

					if (sensorData.length > 0) {
						tempBaselinePlotData.labels = sensorData
							.map((sd) => sd.data.map((dp) => dp[0]))
							.flat()

						tempBaselinePlotData.datasets.push({
							label: tempName,
							data: sensorData
								.map((sd) => sd.data.map((dp) => dp[1]))
								.flat()
								.map((d) => d),
							borderColor: baselineSelectorColor,
							backgroundColor: baselineSelectorColor,
							borderWidth: 1,
							showLine: false,
							animation: false, //helps with rendering performance
							pointRadius: 0, //helps with rendering performance
							spanGaps: true, //helps with rendering performance
							fill: {
								target: { value: 0 },
								below: `#81bf44BB`,
								above: `#f7844388`,
							},
							yAxisID: baselineUnit.includes("L/h") ? "y1" : "y2",
						})
						baselineColorIndex += 1
					}
				}
			}
		}

		const compassWeatherDataTemp: CompassTimeseriesData[] = []
		const weatherSensorsId = weatherSensorIds

		if (weatherSensorsId !== undefined) {
			for (const [index, sensorId] of weatherSensorsId.entries()) {
				const sensorData: SensorData[] = []
				for (const tsd of timeseriesWeatherData.sensors) {
					if (tsd.id === sensorId) {
						sensorData.push(tsd)
					}
				}
				if (sensorData.length > 0) {
					compassWeatherDataTemp.push({
						sensorId: sensorId,
						data: sensorData
							.map((sd) =>
								sd.data.map((dp) => {
									return {
										timestamp: dp[0],
										value: dp[1] === null ? 0 : dp[1],
									}
								})
							)
							.flat(),
					})
				}
			}
		}

		setConditionLinePlotOptions(conditionLinePlotOptionsTemp)
		setConditionLinePlotData(tempConditionPlotData)
		setMinLoad(Math.floor(minLoadTemp > 100000000 ? 950 : minLoadTemp * 0.95))
		setMinBaseline(Math.floor(minBaselineTemp * 0.95))
		setLoadLinePlotData(tempLoadPlotData)
		if (minProducerLoadTemp > 100000000) {
			setMinFuel(0)
			setMaxFuel(1)
		} else {
			setMinFuel(Math.floor(minProducerLoadTemp * 0.95))
			setMaxFuel(undefined)
		}
		setFuelLinePlotData(tempFuelPlotData)
		setBaselinePlotData(tempBaselinePlotData)

		setCompasWeatherData(compassWeatherDataTemp)
	}, [
		timeseriesDataWithBaseline,
		opModesList,
		timeseriesWeatherData,
		showOpModeBg,
		conditionBgColors,
		fishModeBgRanges,
		sensorPair,
		fuelConsumerSensorIds,
	])

	const [isPlotConfigOpen, setIsPlotConfigOpen] = useState<boolean>(false)
	const [plotsData, setPlotsData] = useState<PlantPlot[]>([])
	// const [plotsDataNoReRender, setPlotsDataNoReRender] = useState<PlantPlot[]>([])

	const createPlotsData = async () => {
		const opModeId = representativeOpModeId
		if (opModeId === undefined) {
			return
		}
		const abortController = new AbortController()
		try {
			const response = await requestApi2(
				endpoints.fuelPageGetOpModePlots,
				{
					opModeId: opModeId,
				},
				abortController.signal
			)
			if (response === null) {
				throw new Error()
			}
			setPlotsData(response)
			// setPlotsDataNoReRender(response)
		} catch (err) {
			if (!abortController.signal.aborted) {
				throw err
			}
		}
	}

	useEffect(() => {
		createPlotsData()
	}, [])

	const defaultPlots = async () => {
		for (const plot of plotsData) {
			try {
				await requestApi2(endpoints.plantPlotRemove, { id: plot.id })
			} catch (error) {}
		}
		setPlotsData([])
	}

	const handleAddPlotOpenToggle = () => {
		togglePlotConfig()
	}
	const togglePlotConfig = () => {
		setIsPlotConfigOpen((prev) => !prev)
	}

	const addPlot = async (newPlotData: PlantPlot) => {
		const plotsDataCopy: PlantPlot[] = JSON.parse(JSON.stringify(plotsData))
		const idx = plotsDataCopy.findIndex((plotData) => plotData === newPlotData)
		const opModeId = representativeOpModeId
		if (opModeId === undefined) {
			return
		}
		if (idx === -1) {
			// check if plot is new
			const plotsDataTemp: PlantPlot[] = JSON.parse(JSON.stringify(plotsData))

			try {
				const response = await requestApi2(endpoints.fuelPageAddOpModePlot, {
					opModeId: opModeId,
					plotName:
						newPlotData.name !== null
							? newPlotData.name
							: newPlotData.signals?.[0]?.sensorId !== undefined
								? (common.sensorsById.get(newPlotData.signals?.[0]?.sensorId)?.[0]
										?.display_name ?? t("UNNAMED PLOT"))
								: t("UNNAMED PLOT"),
					signals: newPlotData.signals,
				})
				if (response === null) {
					throw new Error()
				}
				newPlotData.id = response.id
				// fetchPlantPlots()
				// handleConfigOpen()
			} catch (err) {
				console.log(err)
			}
			plotsDataTemp.unshift(newPlotData)
			// setPlotsDataNoReRender(plotsDataTemp)
			setPlotsData(plotsDataTemp)
		}
	}

	const handleDeletePlot = async (inPlotData: PlantPlot) => {
		if (plotsData !== undefined) {
			const plotsDataCopy: PlantPlot[] = JSON.parse(JSON.stringify(plotsData))
			const newPlotsData = plotsDataCopy.filter((plotData) => plotData.id !== inPlotData.id)
			// setPlotsDataCurrent(newPlotsData)

			await requestApi2(endpoints.plantPlotRemove, {
				id: inPlotData.id,
			})
			// fetchPlantPlots()
			// handleConfigOpen()

			// setPlotsDataNoReRender(newPlotsData)
			setPlotsData(newPlotsData)
		}
	}

	const [plotOrder, setPlotOrder] = useState([1, 3, 5, 6])

	useEffect(() => {
		const newOrder: number[] = []
		const plotOrder_0 = plotOrder[0]
		const plotOrder_1 = plotOrder[1]
		const plotOrder_2 = plotOrder[2]
		const plotOrder_3 = plotOrder[3]

		// Check each plot's availability and adjust the order accordingly
		if (
			conditionLinePlotData !== undefined &&
			conditionLinePlotData.datasets.length > 0 &&
			plotOrder_0 !== undefined
		) {
			newOrder.push(1) // conPlot is 1
		}
		if (
			fuelLinePlotData !== undefined &&
			fuelLinePlotData.datasets.length > 0 &&
			plotOrder_1 !== undefined
		) {
			newOrder.push(3) // fuelPlot is 3
		}
		if (
			baselinePlotData !== undefined &&
			baselinePlotData.datasets.length > 0 &&
			plotOrder_2 !== undefined
		) {
			newOrder.push(5) // basePlot is 5
		}
		if (
			loadLinePlotData !== undefined &&
			loadLinePlotData.datasets.length > 0 &&
			plotOrder_3 !== undefined
		) {
			newOrder.push(6) // loadPlot is 6
		}

		// For each unavailable plot, add it to the end of the newOrder array
		if (
			!(conditionLinePlotData !== undefined && conditionLinePlotData.datasets.length > 0) &&
			plotOrder_0 !== undefined
		)
			newOrder.push(1)
		if (
			!(fuelLinePlotData !== undefined && fuelLinePlotData.datasets.length > 0) &&
			plotOrder_1 !== undefined
		)
			newOrder.push(3)
		if (
			!(baselinePlotData !== undefined && baselinePlotData.datasets.length > 0) &&
			plotOrder_2 !== undefined
		)
			newOrder.push(5)
		if (
			!(loadLinePlotData !== undefined && loadLinePlotData.datasets.length > 0) &&
			plotOrder_3 !== undefined
		)
			newOrder.push(6)

		setPlotOrder(newOrder)
	}, [conditionLinePlotData, fuelLinePlotData, baselinePlotData, loadLinePlotData])

	const [layersData, setLayersData] = useState<LayerData[]>()
	const [vesselData, setVesselData] = useState<PlantInfoValidated[]>()

	const [timelineEventMarker, setTimelineEventMarker] = useState<TimelineEventMarker>({
		heading: undefined,
		latestOperationMode: undefined,
		coords: undefined,
	})

	const [
		textFilteredFishGeoJson,
		mapParametersFilteredFishGeoJson,
		isFishFarmDataLoading,
		handleSetTextFilteredFishGeoJson,
	] = useGetFishFarmDataForMap(null)
	const diseaseZonesData = useAppSelector(getDiseaseZones)
	const selectedDiseaseZones = useAppSelector(getSelectedDiseaseZones)
	const fieldsData = useGetOilGasDataForMap()

	const { zoom } = useAppSelector(getMapParamsState)

	useEffect(() => {
		if (vesselTrack === undefined || opModesList === undefined) {
			return
		}
		const points = vesselTrack?.locations
		if (points === undefined) {
			return
		}
		const lastPoint = points[points.length - 1]
		if (lastPoint === undefined) {
			return
		}
		const vesselDataTemp: PlantInfoValidated[] = [
			{
				data: "",
				position: {
					cog: lastPoint.course,
					heading: lastPoint.heading ?? lastPoint.course,
					sog: lastPoint.speed,
					lat: lastPoint.latitude,
					lon: lastPoint.longitude,
					rot: 0,
					navstat: 0,
					srid: 0,
					timeStamp: lastPoint?.timestamp.toISOString() ?? "",
				},
				plantId: plantData?.id ?? -1,
				condition: "green",
				name: paramSelectedVessel ?? "unkown",
				owner: currentCompany ?? "unknown",
			},
		]

		const segments: Segment[] = eventData.map((e) => {
			const eventCombinedModeMetaData = findWhichCombineMode(
				opModesList ?? [],
				e.metadata.opModeId
			)
			const opModeName = findOpModeName(e.metadata.opModeId)
			const color = getColorOfOpModeName(opModeName ?? "unknown") ?? "rgba(77, 83, 170, 0.3)"

			return {
				timestamp: e.event.t2.toISOString(),
				color: setCssColorAlpha(color, 1),
			}
		})

		const layersDataTemp: LayerData[] = [
			{
				mapLayerName: MapLayers.FACILITIES_RADIUS,
				facilitiesRadius: {
					company: appStructure != null ? (Object.keys(appStructure)[0] ?? "") : "",
					facilities:
						facilitiesData !== null && facilitiesData !== undefined
							? facilitiesData
							: [],
				},
				portalFeature: PortalFeature.FACILITIES,
			},
			{
				mapLayerName: MapLayers.VESSELS_NOT_CLICKABLE,
				vesselsNotClickableData: { vessels: vesselDataTemp, zoom: zoom?.zoomLevel },
			},
			{
				mapLayerName: MapLayers.VESSELS_TRACK_SEGMENTS,
				vesselsTrackSegmentsData: {
					intervalPoints: vesselTrack?.locations ?? [],
					operationModeEvents: {
						segments: segments.length > 0 ? segments : undefined,
					},
					zoom: zoom?.zoomLevel,
				},
				portalFeature: PortalFeature.VESSELS_TRACK,
			},
			{
				mapLayerName: MapLayers.TIMELINE_EVENT_MARKER,
				timelineEventMarkerData: timelineEventMarker,
			},
			// {
			// 	mapLayerName: MapLayers.OIL_AND_GAS,
			// 	oilAndGasData: getFacilityFixed(),
			// 	portalFeature: PortalFeature.OIL_AND_GAS,
			// },
			{
				mapLayerName: MapLayers.OIL_AND_GAS_FIELDS,
				oilAndGasFieldsData: fieldsData,
				portalFeature: PortalFeature.OIL_AND_GAS,
			},
			{
				mapLayerName: MapLayers.FISH_FARMS,
				fishFarmsData: textFilteredFishGeoJson,
				portalFeature: PortalFeature.FISH_FARM,
			},
			{
				mapLayerName: MapLayers.DESEASE_ZONES,
				deseaseZonesData: { diseaseZonesData, selectedDiseaseZones },
				portalFeature: PortalFeature.FISH_FARM,
			},
		]
		setLayersData(layersDataTemp)
		setVesselData(vesselDataTemp)
	}, [
		zoom,
		vesselTrack,
		eventData,
		opModesList,
		fieldsData,
		timelineEventMarker,
		facilitiesData,
		textFilteredFishGeoJson,
		selectedDiseaseZones,
	])

	const opModesOrderRef = [28, 29, 30, 31]

	useEffect(() => {
		if (updateChart === true && lastZoomScale !== undefined) {
			updateChartZoom(conditionChartRef, lastZoomScale)
			updateChartZoom(fuelChartRef, lastZoomScale)
			updateChartZoom(baselineChartRef, lastZoomScale)
			updateChartZoom(loadChartRef, lastZoomScale)
			setUpdateChart(false)
		}
	}, [updateChart, lastZoomScale])

	const [startTourFn, setStartTourFn] = useState<(() => void) | undefined>(undefined)

	const handleStartTour = useCallback((fn: () => void) => {
		setStartTourFn(() => fn)
	}, [])

	const tUserManual = useTranslation("userManual").t
	const steps: Step[] = useMemo(() => operationalEventSteps(tUserManual), [i18n.language])
	const scrollRules = useMemo(() => {
		return operationalEventScrollRules
	}, [])

	const isInitialTimelineDataLoading =
		isVesselTrackLoading === true ||
		isTimeseriesDataLoading === true ||
		isTimeseriesWeatherDataLoading === true
	const isExpandedTimelinePreparing =
		isInitialTimelineDataLoading === false &&
		timeseriesData !== undefined &&
		timeseriesData !== null &&
		timeseriesDataWithBaseline === undefined
	const canRenderExpandedTimeline =
		baselineData !== null && timeseriesDataWithBaseline !== undefined

	return (
		// <SensorsGroupContext.Provider value={contextValue}>
		<>
			<UserManual
				moduleName="operations"
				onStartTour={handleStartTour}
				stepsFromOutside={steps}
				scrollRules={scrollRules}
				disableScrolling={true}
			/>
			<div
				className={`border-b-dipai-text-disabled relative h-auto w-full border-b-1 ${
					isInitialTimelineDataLoading === false ? `py-4 pr-4 pl-10` : ``
				}`}
			>
				{isInitialTimelineDataLoading === false ? (
					<>
						<div className={`flex flex-col`}>
							{hasOperationModeEvents && opModesList !== undefined ? (
								<div className={`mb-2 flex h-8 w-full flex-row items-center gap-4`}>
									<p>{`${t(
										combinedModeMetaData?.combinedModeName !== undefined
											? `${combinedModeMetaData.combinedModeName.toUpperCase()} MODES`
											: "OPERATION MODES"
									)}:`}</p>
									<div className={`flex flex-row items-center gap-4`}>
										{opModeColorsMap
											.sort(
												(a, b) =>
													opModesOrderRef.indexOf(a) -
													opModesOrderRef.indexOf(b)
											)
											.map((opModeId, index) => (
												<div
													key={opModeId}
													className={`flex flex-row items-center gap-2`}
												>
													<div
														className={`h-4 w-10 text-sm first-letter:uppercase`}
														style={{
															backgroundColor:
																modeColors[
																	findOpModeName(
																		opModeId
																	)?.toLowerCase() ?? "(na)"
																],
														}}
													/>
													<p className={"text-sm first-letter:uppercase"}>
														{findOpModeName(opModeId)?.split(
															combinedModeMetaData?.splitString ??
																"don't split"
														)?.[1] ??
															findOpModeName(opModeId) ??
															t("UNRECOGNIZED MODE")}
													</p>
												</div>
											))}
									</div>
									<div className={`flex items-center gap-2`}>
										<TooltipButton
											customContent={t(
												showOpModeBg === true
													? "HIDE OPERATION MODES COLORS"
													: "SHOW OPERATION MODES COLORS"
											)}
										>
											<IconButton
												variant={"text-dark"}
												className={"h-7 w-7"}
												onClick={() =>
													setShowOpModeBg !== undefined &&
													setShowOpModeBg((prev) =>
														prev !== undefined ? !prev : undefined
													)
												}
											>
												<IconButton.Icon>
													{showOpModeBg === true
														? MdVisibility
														: MdVisibilityOff}
												</IconButton.Icon>
											</IconButton>
										</TooltipButton>
									</div>
								</div>
							) : null}
							{hasFishModeInRange ? (
								<div className="mb-2 flex min-h-8 w-full flex-row items-center gap-4">
									<p>{`${t("FISH MODES")}:`}</p>
									<div className="flex flex-row flex-wrap items-center gap-4">
										{fishModeLegendItems.map((legendItem) => (
											<div
												key={`fish-legend-${legendItem.label}`}
												className="flex flex-row items-center gap-2"
											>
												<div
													className="h-4 w-10 text-sm first-letter:uppercase"
													style={{
														backgroundColor: legendItem.color,
													}}
												/>
												<p className="text-sm first-letter:uppercase">
													{legendItem.label}
												</p>
											</div>
										))}
									</div>
								</div>
							) : null}
							<div className={`relative w-full min-w-137.5 gap-4`}>
								{canRenderExpandedTimeline ? (
									<>
										<div
											id={"header-operational-event"}
											className={`text-title5 hover:bg-dipai-secondary-100 text-dipai-secondary-800 absolute top-0 right-0 z-10 flex animate-[bounce_1s_4] flex-row items-center self-center rounded-sm p-1 font-bold whitespace-nowrap ${
												startTourFn !== undefined
													? "hover:text-dipai-secondary-901 cursor-pointer transition-colors"
													: "hidden"
											}`}
											onClick={startTourFn}
										>
											<MdAutoStories className={`size-6`} />
										</div>
										<div
											className={`mb-5 grid grid-cols-1 gap-4 2xl:grid-cols-2`}
										>
											{/* Condition Line Plot */}
											<div className={`order-1 2xl:order-${plotOrder[0]}`}>
												<p className="mb-4">
													{t("VESSEL OPERATION TIMELINE")}
												</p>
												<div
													className="h-71"
													id="operational-event-performance-plot"
												>
													<ChartContainer
														chartRef={conditionChartRef}
														options={conditionLinePlotOptions}
														plotData={conditionLinePlotData}
														heightExpression={`calc(250px + 34px)`}
														// heightExpression={`calc(${
														// 	nVesselPlots <= 2 ? `588px` : `250px + 34px`
														// })`}
													/>
												</div>
											</div>
											{/* Map */}
											<div className="z-9 order-5 2xl:order-2">
												<p className="mb-4">{t("ROUTE TIMELINE")}</p>
												{currentCompany !== undefined &&
												timeseriesDataWithBaseline !== undefined &&
												layersData !== undefined &&
												vesselData !== undefined ? (
													<div
														id="operational-event-map"
														className={`relative overflow-hidden rounded-sm`}
														style={{
															height: `284px`,
														}}
													>
														<RegularMapComponent3
															customerName={currentCompany}
															layersData={layersData}
															mapDivId={`mapDiv_${t1EventRange.toISOString()}_${t2EventRange.toISOString()}`}
															inputsForBoundCalc={{
																shipData: vesselData,
																trackData: layersData
																	.find(
																		(ld) =>
																			ld.mapLayerName ===
																			MapLayers.VESSELS_TRACK_SEGMENTS
																	)
																	?.vesselsTrackSegmentsData?.intervalPoints?.map(
																		(p) => {
																			return {
																				type: "Feature" as const,
																				properties: {},
																				geometry: {
																					type: "Point" as const,
																					coordinates: [
																						p.longitude,
																						p.latitude,
																					],
																				},
																			}
																		}
																	),
															}}
														/>
													</div>
												) : null}
											</div>
											{/* Consumers */}
											<div
												id="operational-event-generators-plot"
												className={`order-2 max-h-71 2xl:order-${plotOrder[1]}`}
											>
												<ChartContainer
													chartRef={fuelChartRef}
													options={fuelLinePlotOptions}
													plotData={fuelLinePlotData}
													minY={minFuel}
													maxY={maxFuel}
													heightExpression={`calc(250px + 34px)`}
													// heightExpression={`calc(${
													// 	nVesselPlots <= 1 ? `584px` : `250px + 34px`
													// })`}
												/>
											</div>
											{/* Weather */}
											<div
												id="operational-event-environmental-factors-plot"
												className={`order-6 max-h-71 2xl:order-4`}
											>
												<div className="item-center flex flex-row justify-evenly rounded-sm border-1">
													<CompassWind
														width={250}
														t1={t1EventRange}
														time={time}
														data={compassWeatherData}
														heading={
															timelineEventMarker.heading ??
															layersData?.find(
																(ld) =>
																	ld.mapLayerName ===
																	MapLayers.VESSELS_NOT_CLICKABLE
															)?.vesselsNlData?.vesselInfo?.[0]
																?.position?.heading
														}
													/>
													<CompassCurrent
														width={350}
														t1={t1EventRange}
														time={time}
														data={compassWeatherData}
														heading={
															timelineEventMarker.heading ??
															layersData?.find(
																(ld) =>
																	ld.mapLayerName ===
																	MapLayers.VESSELS_NOT_CLICKABLE
															)?.vesselsNlData?.vesselInfo?.[0]
																?.position?.heading
														}
													/>
												</div>
											</div>
											{/* Fuel */}
											<div
												id="operational-event-dynamic-baseline-plot"
												className={`order-3 max-h-71 2xl:order-${plotOrder[2]}`}
											>
												<ChartContainer
													chartRef={baselineChartRef}
													options={baselinePlotOptions}
													plotData={baselinePlotData}
													minY={minBaseline}
													heightExpression={`calc(250px + 34px)`}
												/>
											</div>
											{/* Thrusters */}
											<div
												id="operational-event-power-consumption-plot"
												className={`order-4 max-h-71 2xl:order-${plotOrder[3]}`}
											>
												<ChartContainer
													chartRef={loadChartRef}
													options={loadLinePlotOptions}
													plotData={loadLinePlotData}
													minY={minLoad}
													heightExpression={`calc(250px + 34px)`}
												/>
											</div>
										</div>
									</>
								) : isExpandedTimelinePreparing ? (
									<div className="mb-4 flex h-8 w-full flex-row items-center gap-2">
										<LoadingCircleSimples
											size={24}
											textColorTailwind="text-dipai-secondary-500"
										/>
									</div>
								) : (
									<div className="text-menu mb-4 flex h-8 w-full flex-row items-center gap-2 rounded-sm border-1 border-red-300 bg-red-50 px-2 font-medium text-red-700">
										<ImportantIcon
											bgColor="text-red-300"
											iconColor="text-red-700"
										/>
										{t(
											"THERE WAS AN ERROR GETTING DATA FOR THIS VESSEL AND/OR OPERATION."
										)}
									</div>
								)}
							</div>
						</div>
						<div id="operational-event-chart-customization">
							<div className="flex w-auto flex-row items-center justify-start gap-4 py-3">
								<p>{t("ANALYSIS")}</p>
								<Button
									size={"medium"}
									variant={"outline-dark"}
									className={`w-auto min-w-30 px-2`}
									onClick={defaultPlots}
								>
									<Button.Label>{t("RESET PLOTS")}</Button.Label>
								</Button>
								<Button
									size={"medium"}
									variant={"filled-secondary"}
									className={`w-auto min-w-30 px-2`}
									onClick={handleAddPlotOpenToggle}
								>
									<Button.Label>{t("ADD PLOT")}</Button.Label>
								</Button>
							</div>
							{plotsData !== undefined && currentCompany !== undefined ? (
								plotsData.map((plotData, index) => (
									<PlotCard
										key={`plotCard_${index}`}
										fetchPlantPlots={createPlotsData}
										companyName={currentCompany}
										plantName={paramSelectedVessel}
										componentName={"All Assets"}
										plotData={plotData}
										width={"calc(100% - 0px)"}
										margin={"0px"}
										hideRangeSelector={true}
										listOfModeColors={modeColors}
										from={t1EventRange}
										to={t2EventRange}
									/>
								))
							) : (
								<div className="text-menu grid h-auto w-full grid-cols-[24px_auto] items-center gap-3 rounded-sm border-1 border-red-300 bg-red-50 px-3 py-1 font-medium text-red-700">
									<ImportantIcon
										bgColor="text-red-300"
										iconColor="text-red-700"
									/>
									{t(
										"AN ERROR OCCURRED SERVER-SIDE WHEN RETRIEVING THE DATA FOR THIS PLOT. PLEASE CONTACT DIPAI SUPPORT."
									)}
								</div>
							)}
						</div>
					</>
				) : (
					<div className={`absolute -top-10.5 left-5.25`}>
						<LoadingCircleSimples
							size={32}
							textColorTailwind="text-dipai-secondary-500"
						/>
					</div>
				)}
				<Modal
					isOpen={isPlotConfigOpen}
					setIsOpen={setIsPlotConfigOpen}
				>
					{currentCompany !== undefined ? (
						<PlotConfig
							setPlotData={addPlot}
							plotData={{ name: null, id: -1, signals: [] }}
							handleConfigOpen={togglePlotConfig}
							companyName={currentCompany}
							plantName={paramSelectedVessel}
							componentName={"All Assets"}
							addNewPlot={true}
							configType={"Plot"}
							emptyLineTemplate={{
								sensorId: -1,
								aggregationMethod: AggregationMethod.SNAP_FIRST,
								baselines: [],
								id: Math.random(),
								thresholds: [],
								color: "",
							}}
							columnsLayout={`24px auto 250px 136px 48px`}
							numberSignals={6}
						/>
					) : (
						<div className="text-menu grid h-auto w-full grid-cols-[24px_auto] items-center gap-3 rounded-sm border-1 border-red-300 bg-red-50 px-3 py-1 font-medium text-red-700">
							<ImportantIcon
								bgColor="text-red-300"
								iconColor="text-red-700"
							/>
							{t(
								"AN ERROR OCCURRED SERVER-SIDE WHEN RETRIEVING THE DATA FOR THIS PLOT. PLEASE CONTACT DIPAI SUPPORT."
							)}
						</div>
					)}
				</Modal>
			</div>
		</>
		// </SensorsGroupContext.Provider>
	)
}

export default function OperationsTimelineExpandedTableArea({
	row,
}: {
	row: Row<FuelConsumptionTimelineTableItem>
}) {
	const { t } = useTranslation()
	const eventData = row
		.getAllCells()
		.find((c) => c.id.split("_")[1] === "eventData")
		?.getValue() as EventTableEntry[]

	const eventData_0 = eventData?.[0]
	if (eventData === undefined || eventData_0 === undefined) {
		return (
			<div className={`p-4`}>
				<p className={`text-body`}>{t("NO DATA AVAILABLE TO DISPLAY")}</p>
			</div>
		)
	}
	return (
		<ComponentBody
			eventData={eventData}
			eventData_0={eventData_0}
		/>
	)
}
